import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import {
  Plus,
  Search,
  Building2,
  Users as UsersIcon,
  FileText,
  Plug,
  MoreVertical,
  Trash2,
  Eye,
  RefreshCw,
  Upload,
  X,
  Image as ImageIcon,
  Info,
  CheckCircle2,
  BookOpen,
  BarChart3,
  Settings,
  Loader2,
  CornerDownRight,
  Check,
  ChevronsUpDown,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useNavigate } from 'react-router-dom';
import { useApi, ApiError } from '@/hooks/useApi';
import { useCompaniesList } from '@/hooks/useCompaniesData';
import { useCompanyWithProfile } from '@/hooks/useCompanyWithProfile';
import { useCompanySummary } from '@/hooks/useCompanySummary';
import { useCompany } from '@/contexts/CompanyContext';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import type { CompanyApi, CompanyWithProfileApi, UpdateCompanyRequest, CurrencyApi } from '@/types/api/companies';
import { FINANCIALS_PRODUCT_ID, DIMENSIONS_VNEXT_PRODUCT_ID } from '@/types/api/companies';
import { HelpSupportButton } from '@/components/help-support';
import { cn, formatDateTime as formatDateTimeShared } from '@/lib/utils';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useMode } from '@/providers/ModeProvider';
import { useScreenModes } from '@/providers/ScreenModesProvider';
import { useBankDetailsCurrencies } from '@/hooks/useCurrenciesData';
import { CurrencyCombobox } from '@/components/ui/currency-combobox';
import {
  BANK_DETAIL_MAX_LENGTHS,
  formatIbanDisplay,
  hasBankDetailErrors,
  stripIbanSpaces,
  validateBankDetailRow,
  type BankDetailFieldErrors,
} from '@/lib/validation/bankDetails';
import { useIsAdmin } from '@/components/auth/useIsAdmin';
import { AdminOnlyEmptyState } from '@/components/auth/admin-only-empty-state';
import { validateEmail, validatePhone, validateUkVat, validateUkCrn } from '@/lib/validators/company-details';

type StatusFilter = 'all' | 'active' | 'pending_setup';
type SortOption = 'name-asc' | 'name-desc' | 'newest' | 'oldest';

interface Company {
  id: string;
  name: string;
  status: 'active' | 'pending_setup';
  logo?: string;
  description?: string;
  integrationStatus: 'connected' | 'partial' | 'none';
  userCount: number;
  createdAt: string;
  lastSyncedAt?: string;
  linkedEvoSubscriptionId?: string;
  linkedSubscriptionName?: string;
  linkedProductName?: string;
  linkedProductId?: string;
  currencyCode?: string;
  code?: string;
  taxId?: string;
  linkedSubLedger?: string;
  externalCompanyName?: string;
  address?: string;
  vatRegistrationNumber?: string;
  companyRegistrationNumber?: string;
  phone?: string;
  email?: string;
  companyGroupId?: number;
  isMultiCompany?: boolean;
}

type CompanyRow = Company & { isSubCompany: boolean };

interface Instance {
  instanceId: string;
  name: string;
  organisationId: string;
  organisationName: string;
  databaseName: string | null;
  datasetId: string | null;
}

interface BankDetail {
  id: string;
  serverId?: number;
  currency: string;
  sortCode: string;
  accountNumber: string;
  accountName: string;
  ibanNumber: string;
  swiftBicCode: string;
}

function getProductId(product: string): string | undefined {
  if (product === 'Financials') return FINANCIALS_PRODUCT_ID;
  if (product === 'DimensionsvNext') return DIMENSIONS_VNEXT_PRODUCT_ID;
  return undefined;
}

function resetInstanceIdIfMissing(instances: Instance[]) {
  return (currentId: string) => {
    if (currentId && !instances.some((x) => x.instanceId === currentId)) {
      return '';
    }
    return currentId;
  };
}

function formatCompanyDateTime(dateString: string): string {
  return formatDateTimeShared(dateString);
}

function getIntegrationBadgeVariant(status: string | undefined): 'default' | 'secondary' | 'outline' {
  if (status === 'connected') return 'default';
  if (status === 'partial') return 'secondary';
  return 'outline';
}

const DEMO_INSTANCES: Instance[] = [
  {
    instanceId: 'demo-instance-1',
    name: 'Demo Instance 1',
    organisationId: 'demo-org-1',
    organisationName: 'Demo Organisation 1',
    databaseName: null,
    datasetId: null,
  },
  {
    instanceId: 'demo-instance-2',
    name: 'Demo Instance 2',
    organisationId: 'demo-org-2',
    organisationName: 'Demo Organisation 2',
    databaseName: null,
    datasetId: null,
  },
  {
    instanceId: 'demo-instance-3',
    name: 'Demo Instance 3 (No Sub Ledgers)',
    organisationId: 'demo-org-3',
    organisationName: 'Demo Organisation 3',
    databaseName: null,
    datasetId: null,
  },
];

interface SubLedgerOption {
  code: string;
  name: string;
}

const SUBLEDGER_NONE_VALUE = '__none__';

const DEMO_SUB_LEDGERS: SubLedgerOption[] = [
  { code: 'SL1', name: 'Sub Ledger 1' },
  { code: 'SL2', name: 'Sub Ledger 2' },
  { code: 'SL3', name: 'Sub Ledger 3' },
  { code: 'SL4', name: 'Sub Ledger 4' },
  { code: 'SL5', name: 'Sub Ledger 5' },
];

interface FetchInstancesCallbacks {
  setInstances: (instances: Instance[]) => void;
  setInstanceId: (updater: (currentId: string) => string) => void;
  setLoading: (loading: boolean) => void;
  setError?: (error: string | null) => void;
}

function fetchInstancesForProduct(
  product: string,
  isDemo: boolean,
  apiGet: <T>(url: string) => Promise<T>,
  callbacks: FetchInstancesCallbacks,
) {
  if (product !== 'Financials' && product !== 'DimensionsvNext') {
    callbacks.setInstances([]);
    callbacks.setInstanceId(() => '');
    return;
  }

  callbacks.setLoading(true);
  callbacks.setError?.(null);
  const productId = getProductId(product);

  if (isDemo) {
    setTimeout(() => {
      callbacks.setInstances(DEMO_INSTANCES);
      callbacks.setInstanceId(resetInstanceIdIfMissing(DEMO_INSTANCES));
      callbacks.setLoading(false);
    }, 300);
    return;
  }

  apiGet<Instance[]>(`/api/v1/externalInstances?productId=${productId}`)
    .then((data) => {
      callbacks.setInstances(data);
      callbacks.setInstanceId(resetInstanceIdIfMissing(data));
      callbacks.setLoading(false);
    })
    .catch((error) => {
      console.error('Error fetching instances:', error);
      callbacks.setError?.('Failed to load instances. Please try again.');
      callbacks.setLoading(false);
    });
}

interface FetchSubLedgersCallbacks {
  setSubLedgers: (subLedgers: SubLedgerOption[]) => void;
  setLoading: (loading: boolean) => void;
  setError?: (error: string | null) => void;
}

function fetchSubLedgersForInstance(
  instanceId: string,
  product: string,
  isDemo: boolean,
  apiGet: <T>(url: string) => Promise<T>,
  callbacks: FetchSubLedgersCallbacks,
) {
  if (!instanceId || !product) {
    callbacks.setSubLedgers([]);
    return;
  }

  callbacks.setLoading(true);
  callbacks.setError?.(null);

  if (isDemo) {
    setTimeout(() => {
      const demoSubLedgers = instanceId === 'demo-instance-3' ? [] : DEMO_SUB_LEDGERS;
      callbacks.setSubLedgers(demoSubLedgers);
      callbacks.setLoading(false);
    }, 300);
    return;
  }

  const productId = getProductId(product);
  if (!productId) {
    callbacks.setSubLedgers([]);
    return;
  }

  apiGet<SubLedgerOption[]>(`/api/v1/externalInstances/${instanceId}/subcompanies?productId=${productId}`)
    .then((data) => {
      callbacks.setSubLedgers(data);
      callbacks.setLoading(false);
    })
    .catch((error) => {
      console.error('Error fetching sub ledgers:', error);
      callbacks.setError?.('Failed to load sub ledgers. Please try again.');
      callbacks.setLoading(false);
    });
}

function buildBankDetailsPayload(rows: BankDetail[]) {
  const nonEmpty = rows.filter(
    (d) => d.currency || d.sortCode || d.accountNumber || d.accountName || d.ibanNumber || d.swiftBicCode,
  );
  return nonEmpty.map((row) => ({
    currency: row.currency || null,
    sortCode: row.sortCode || null,
    accountNumber: row.accountNumber || null,
    accountName: row.accountName || null,
    ibanNumber: row.ibanNumber ? stripIbanSpaces(row.ibanNumber) : null,
    swiftBicCode: row.swiftBicCode || null,
  }));
}

interface DetailsPayloadInput {
  address: string;
  vatRegistrationNo: string;
  companyRegistrationNo: string;
  phone: string;
  email: string;
}

function buildDetailsPayload(formData: DetailsPayloadInput) {
  return {
    addressLine: formData.address || null,
    country: null,
    vatNumber: formData.vatRegistrationNo || null,
    companyRegistrationNumber: formData.companyRegistrationNo || null,
    phone: formData.phone || null,
    email: formData.email || null,
  };
}

function mapFetchedToCompanies(fetched: Company[]): Company[] {
  return fetched.map((c) => ({
    id: c.id,
    name: c.name,
    status: c.status,
    logo: c.logo,
    description: c.description,
    integrationStatus: c.integrationStatus,
    userCount: c.userCount,
    createdAt: c.createdAt,
    lastSyncedAt: c.lastSyncedAt,
    linkedEvoSubscriptionId: c.linkedEvoSubscriptionId,
    linkedSubscriptionName: c.linkedSubscriptionName,
    linkedProductName: c.linkedProductName,
    linkedProductId: c.linkedProductId,
    currencyCode: c.currencyCode,
    code: c.code,
    taxId: c.taxId,
    linkedSubLedger: c.linkedSubLedger,
    externalCompanyName: c.externalCompanyName,
    address: c.address,
    vatRegistrationNumber: c.vatRegistrationNumber,
    companyRegistrationNumber: c.companyRegistrationNumber,
    phone: c.phone,
    email: c.email,
    companyGroupId: c.companyGroupId,
    isMultiCompany: c.isMultiCompany,
  }));
}

function sortCompanies(a: Company, b: Company, sortOption: string): number {
  switch (sortOption) {
    case 'name-asc':
      return a.name.localeCompare(b.name);
    case 'name-desc':
      return b.name.localeCompare(a.name);
    case 'newest':
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    case 'oldest':
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    default:
      return 0;
  }
}

function buildFilteredAndSortedCompanies(
  companies: Company[],
  searchTerm: string,
  statusFilter: StatusFilter,
  sortOption: SortOption,
): CompanyRow[] {
  let filtered = companies;

  if (searchTerm) {
    filtered = filtered.filter((c) => c.name.toLowerCase().includes(searchTerm.toLowerCase()));
  }

  if (statusFilter !== 'all') {
    filtered = filtered.filter((c) => c.status === statusFilter);
  }

  // Sort parents only (sub-companies will follow their parent)
  const parents = filtered.filter((c) => !(c.isMultiCompany && c.externalCompanyName != null));
  const sortedParents = [...parents].sort((a, b) => sortCompanies(a, b, sortOption));

  // Interleave sub-companies immediately after their parent
  const result: CompanyRow[] = [];
  const addedIds = new Set<string>();

  for (const parent of sortedParents) {
    result.push({ ...parent, isSubCompany: false });
    addedIds.add(parent.id);

    if (parent.isMultiCompany && parent.companyGroupId !== undefined) {
      const children = filtered.filter(
        (c) =>
          c.isMultiCompany &&
          c.companyGroupId === parent.companyGroupId &&
          c.externalCompanyName != null &&
          !addedIds.has(c.id),
      );
      for (const child of children) {
        result.push({ ...child, isSubCompany: true });
        addedIds.add(child.id);
      }
    }
  }

  // Any remaining filtered companies not yet added (orphaned sub-companies whose parent was filtered out)
  for (const company of filtered) {
    if (!addedIds.has(company.id)) {
      result.push({ ...company, isSubCompany: false });
    }
  }

  return result;
}

// ─── Product availability probing ────────────────────────────────────────────

type ProductAvailability = 'loading' | 'available' | 'unavailable';

interface AvailableProducts {
  Financials: ProductAvailability;
  DimensionsvNext: ProductAvailability;
}

/**
 * Probes both products in parallel to determine which ones have accessible
 * instances. Returns a settled record so callers can react immediately.
 */
async function probeAvailableProducts(
  isDemo: boolean,
  apiGet: <T>(url: string) => Promise<T>,
): Promise<{ Financials: boolean; DimensionsvNext: boolean }> {
  if (isDemo) {
    // In demo mode both products are always available
    return { Financials: true, DimensionsvNext: true };
  }

  const check = async (productId: string): Promise<boolean> => {
    try {
      const data = await apiGet<Instance[]>(`/api/v1/externalInstances?productId=${productId}`);
      return Array.isArray(data) && data.length > 0;
    } catch {
      return false;
    }
  };

  const [financialsOk, dimensionsOk] = await Promise.all([
    check(FINANCIALS_PRODUCT_ID),
    check(DIMENSIONS_VNEXT_PRODUCT_ID),
  ]);

  return { Financials: financialsOk, DimensionsvNext: dimensionsOk };
}

// ─── Product Radio Cards ──────────────────────────────────────────────────────

interface ProductCardProps {
  product: 'Financials' | 'DimensionsvNext';
  label: string;
  description: string;
  selected: boolean;
  onSelect: () => void;
}

function ProductCard({ product, label, description, selected, onSelect }: Readonly<ProductCardProps>) {
  const Icon = product === 'Financials' ? BookOpen : BarChart3;

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'flex flex-col gap-3 rounded-lg border-2 p-4 text-left transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 w-full',
        selected ? 'border-primary bg-primary/5 shadow-sm' : 'border-border hover:border-primary/50 hover:bg-muted/50',
      )}
      aria-pressed={selected}
    >
      <div className="flex items-start justify-between">
        <div
          className={cn(
            'flex h-9 w-9 items-center justify-center rounded-md border',
            selected ? 'border-primary/30 bg-primary/10 text-primary' : 'border-border bg-muted text-muted-foreground',
          )}
        >
          <Icon className="h-5 w-5" />
        </div>
        {selected && <CheckCircle2 className="h-5 w-5 text-primary shrink-0" />}
      </div>
      <div>
        <p className="font-semibold text-sm">{label}</p>
        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{description}</p>
      </div>
    </button>
  );
}

interface ProductRadioCardsProps {
  availability: AvailableProducts;
  selected: string;
  onSelect: (product: string) => void;
}

function ProductRadioCards({ availability, selected, onSelect }: Readonly<ProductRadioCardsProps>) {
  const isLoading = availability.Financials === 'loading' || availability.DimensionsvNext === 'loading';

  const showFinancials = availability.Financials !== 'unavailable';
  const showDimensions = availability.DimensionsvNext !== 'unavailable';

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 gap-3">
        <Skeleton className="h-28 w-full rounded-lg" />
        <Skeleton className="h-28 w-full rounded-lg" />
      </div>
    );
  }

  const visibleCount = (showFinancials ? 1 : 0) + (showDimensions ? 1 : 0);

  if (visibleCount === 0) {
    return (
      <TooltipProvider>
        <p className="text-sm text-muted-foreground">No products are available for your subscription.</p>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
      <div className={cn('grid gap-3', visibleCount === 2 ? 'grid-cols-2' : 'grid-cols-1 max-w-xs')}>
        {showFinancials && (
          <ProductCard
            product="Financials"
            label="Access Financials"
            description="Ledger management, financial reporting and accounts."
            selected={selected === 'Financials'}
            onSelect={() => onSelect('Financials')}
          />
        )}
        {showDimensions && (
          <ProductCard
            product="DimensionsvNext"
            label="Access Dimensions"
            description="Multi-dimensional reporting and advanced analytics."
            selected={selected === 'DimensionsvNext'}
            onSelect={() => onSelect('DimensionsvNext')}
          />
        )}
      </div>
    </TooltipProvider>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

const INITIAL_FORM_DATA = {
  companyId: '',
  name: '',
  address: '',
  vatRegistrationNo: '',
  companyRegistrationNo: '',
  phone: '',
  email: '',
  status: 'active' as 'active' | 'pending_setup',
  logo: '',
  description: '',
  currencyCode: undefined as string | undefined,
  code: '',
  taxId: '',
};

const INITIAL_BANK_DETAIL: BankDetail = {
  id: '1',
  currency: '',
  sortCode: '',
  accountNumber: '',
  accountName: '',
  ibanNumber: '',
  swiftBicCode: '',
};

function validateLogoFile(file: File): string | null {
  const acceptedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/svg+xml'];
  if (!acceptedTypes.includes(file.type)) return 'Please upload an image file (PNG, JPG, GIF, or SVG)';
  if (file.size > 5 * 1024 * 1024) return 'File size must be less than 5MB';
  return null;
}

async function editCompanyAction(
  selectedCompany: Company,
  formData: { name: string; status: string; description: string },
  isDemo: boolean,
  apiPut: (url: string, body: unknown) => Promise<unknown>,
  refetchCompanies: () => Promise<unknown>,
  updateLocal: (updater: (prev: Company[]) => Company[]) => void,
  onDone: () => void,
): Promise<void> {
  try {
    if (isDemo) {
      await new Promise((resolve) => setTimeout(resolve, 300));
      updateLocal((prev) =>
        prev.map((c) =>
          c.id === selectedCompany.id
            ? {
                ...c,
                name: formData.name,
                status: formData.status as 'active' | 'pending_setup',
                description: formData.description,
              }
            : c,
        ),
      );
    } else {
      await apiPut(`/api/v1/company/${selectedCompany.id}`, {
        name: formData.name,
        code: selectedCompany.code,
        taxId: selectedCompany.taxId,
        externalCompanyCode: selectedCompany.linkedSubLedger ?? null,
        externalCompanyName: selectedCompany.externalCompanyName ?? null,
        status: formData.status,
      } satisfies UpdateCompanyRequest);
      await refetchCompanies();
    }
    toast.success('Company updated successfully');
    onDone();
  } catch (error) {
    console.error('Error updating company:', error);
    toast.error('Failed to update company');
  }
}

interface SaveIntegrationParams {
  selectedCompany: Company;
  selectedInstanceId: string;
  selectedProduct: string;
  selectedSubLedger: string;
  subLedgers: SubLedgerOption[];
  instances: Instance[];
  isDemo: boolean;
  apiPut: (url: string, body: unknown) => Promise<unknown>;
  refetchCompanies: () => Promise<unknown>;
  updateLocal: (updater: (prev: Company[]) => Company[]) => void;
  onDone: () => void;
}

/**
 * Saves integration details for an existing company.
 * Integration linking (subscription/product) is set at creation time and is immutable.
 * This function only updates the sub-ledger assignment (ExternalCompanyCode/ExternalCompanyName)
 * on existing companies.
 */
async function saveIntegrationAction(params: SaveIntegrationParams): Promise<void> {
  const {
    selectedCompany,
    selectedInstanceId,
    selectedProduct,
    selectedSubLedger,
    subLedgers,
    instances,
    isDemo,
    apiPut,
    refetchCompanies,
    updateLocal,
    onDone,
  } = params;
  const selectedInstance = instances.find((x) => x.instanceId === selectedInstanceId);
  const instanceName = selectedInstance?.name || undefined;
  const productId = getProductId(selectedProduct);
  const hasSubLedger = selectedSubLedger !== '' && selectedSubLedger !== SUBLEDGER_NONE_VALUE;
  try {
    if (isDemo) {
      await new Promise((resolve) => setTimeout(resolve, 300));
      updateLocal((prev) =>
        prev.map((x) =>
          x.id === selectedCompany.id
            ? {
                ...x,
                linkedEvoSubscriptionId: selectedInstanceId || undefined,
                linkedSubscriptionName: instanceName || undefined,
                linkedProductId: selectedInstanceId ? String(productId) : undefined,
                linkedProductName: selectedInstanceId ? selectedProduct : undefined,
                linkedSubLedger: hasSubLedger ? selectedSubLedger : undefined,
                integrationStatus: selectedInstanceId ? ('connected' as const) : ('none' as const),
              }
            : x,
        ),
      );
    } else {
      await apiPut(`/api/v1/company/${selectedCompany.id}`, {
        name: selectedCompany.name,
        code: selectedCompany.code,
        taxId: selectedCompany.taxId,
        externalCompanyCode: hasSubLedger ? selectedSubLedger : null,
        externalCompanyName: hasSubLedger
          ? subLedgers.find((x) => x.code === selectedSubLedger)?.name || selectedSubLedger
          : null,
        status: selectedCompany.status,
      });
      await refetchCompanies();
    }
    toast.success('Company integration saved successfully');
    onDone();
  } catch (error) {
    console.error('Error saving company integration:', error);
    toast.error('Failed to save company integration');
    onDone();
  }
}

async function syncCompanyAction(
  company: Company,
  isDemo: boolean,
  apiPost: (url: string, body: unknown) => Promise<unknown>,
): Promise<void> {
  try {
    if (isDemo) {
      await new Promise((resolve) => setTimeout(resolve, 300));
    } else {
      await apiPost('/api/v1/sync/all', {});
    }
    toast.success(`Sync queued for ${company.name}`);
  } catch (error) {
    console.error('Error triggering sync:', error);
    toast.error('Failed to trigger sync');
  }
}

interface EditViewFormData {
  companyId: string;
  companyName: string;
  status: 'active' | 'pending_setup';
  taxId: string;
  description: string;
  address: string;
  vatRegistrationNumber: string;
  companyRegistrationNumber: string;
  phone: string;
  email: string;
  logo: string | null;
}

function buildEditFormFromCompany(company: Company): EditViewFormData {
  return {
    companyId: company.code || '',
    companyName: company.name,
    status: company.status,
    taxId: company.taxId || '',
    description: company.description || '',
    address: company.address || '',
    vatRegistrationNumber: company.vatRegistrationNumber || '',
    companyRegistrationNumber: company.companyRegistrationNumber || '',
    phone: company.phone || '',
    email: company.email || '',
    logo: null,
  };
}

function IntegrationStatusBadge({
  status,
  company,
}: Readonly<{ status: Company['integrationStatus']; company?: Company }>) {
  const isPendingSetup = status === 'none' && company?.linkedEvoSubscriptionId;

  switch (status) {
    case 'connected':
      return (
        <Badge className="bg-green-500/10 text-green-500 pointer-events-none">
          <Plug className="h-3 w-3 mr-1" />
          {company?.linkedProductName || 'Connected'}
        </Badge>
      );
    case 'partial':
      return (
        <Badge className="bg-yellow-500/10 text-yellow-500 pointer-events-none">
          <Plug className="h-3 w-3 mr-1" />
          Partial
        </Badge>
      );
    case 'none':
      if (isPendingSetup) {
        return (
          <Badge className="bg-orange-500/10 text-orange-500 pointer-events-none">
            <Plug className="h-3 w-3 mr-1" />
            Pending Setup
          </Badge>
        );
      }
      return (
        <Badge variant="outline" className="pointer-events-none">
          <Plug className="h-3 w-3 mr-1" />
          Not Connected
        </Badge>
      );
  }
}

interface DetailsTabContentProps {
  company: Company | null;
  isEditing: boolean;
  formData: EditViewFormData;
  onFormDataChange: (data: EditViewFormData) => void;
  draggingLogo: boolean;
  onLogoDragOver: (e: React.DragEvent) => void;
  onLogoDragLeave: (e: React.DragEvent) => void;
  onLogoDrop: (e: React.DragEvent) => void;
  onLogoFile: (file: File) => void;
  onLogoDelete: () => void;
  isUploadingLogo: boolean;
  isDeletingLogo: boolean;
  validationErrors?: Record<string, string | null>;
  validationWarnings?: Record<string, string | null>;
  onFieldChange?: (field: string) => void;
  onFieldBlur?: (field: string, value: string) => void;
}

function DetailsTabContent({
  company,
  isEditing,
  formData,
  onFormDataChange,
  draggingLogo,
  onLogoDragOver,
  onLogoDragLeave,
  onLogoDrop,
  onLogoFile,
  onLogoDelete,
  isUploadingLogo,
  isDeletingLogo,
  validationErrors,
  validationWarnings,
  onFieldChange,
  onFieldBlur,
}: Readonly<DetailsTabContentProps>) {
  const renderEditableField = (
    label: string,
    field:
      | 'taxId'
      | 'description'
      | 'address'
      | 'vatRegistrationNumber'
      | 'companyRegistrationNumber'
      | 'phone'
      | 'email',
    placeholder: string,
    colSpan?: boolean,
    type?: string,
  ) => {
    const fieldValue = formData[field];
    const errorMessage = validationErrors?.[field] ?? null;
    const warningMessage = validationWarnings?.[field] ?? null;
    return (
      <div className={colSpan ? 'space-y-2 col-span-2' : 'space-y-2'}>
        <Label className="text-muted-foreground">{label}</Label>
        {isEditing ? (
          <>
            <Input
              type={type || 'text'}
              value={fieldValue}
              onChange={(e) => {
                onFormDataChange({ ...formData, [field]: e.target.value });
                onFieldChange?.(field);
              }}
              onBlur={(e) => onFieldBlur?.(field, e.target.value)}
              placeholder={placeholder}
              aria-invalid={errorMessage ? true : undefined}
            />
            {errorMessage && <p className="text-sm text-destructive">{errorMessage}</p>}
            {!errorMessage && warningMessage && (
              <p className="text-sm text-amber-600 dark:text-amber-500">{warningMessage}</p>
            )}
          </>
        ) : (
          <p className="text-sm font-medium">{fieldValue || '-'}</p>
        )}
      </div>
    );
  };

  const renderLogoSection = () => {
    if (isEditing) {
      const logoBusy = isUploadingLogo || isDeletingLogo;
      return (
        <div className="space-y-2">
          <button
            type="button"
            disabled={logoBusy}
            onDragOver={onLogoDragOver}
            onDragLeave={onLogoDragLeave}
            onDrop={onLogoDrop}
            className={cn(
              'w-full border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors',
              draggingLogo ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50',
              logoBusy && 'opacity-50 cursor-not-allowed',
            )}
            onClick={() => document.getElementById('logo-upload-view')?.click()}
          >
            <input
              id="logo-upload-view"
              type="file"
              accept="image/*"
              className="hidden"
              disabled={logoBusy}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  onLogoFile(file);
                }
                e.target.value = '';
              }}
            />
            {isUploadingLogo ? (
              <Loader2 className="h-8 w-8 mx-auto mb-2 text-muted-foreground animate-spin" />
            ) : (
              <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
            )}
            <p className="text-sm text-muted-foreground">
              {isUploadingLogo ? 'Uploading logo…' : 'Drag and drop your logo here, or click to browse'}
            </p>
            <p className="text-xs text-muted-foreground mt-1">Supports: JPG, PNG, GIF</p>
          </button>
          {(formData.logo || company?.logo) && (
            <div className="flex items-center gap-4 p-4 border rounded-lg">
              <img
                src={formData.logo || company?.logo || ''}
                alt="Company Logo"
                className="h-16 w-16 object-contain border rounded"
              />
              <div className="flex-1">
                <p className="text-sm font-medium">Current logo</p>
                <p className="text-xs text-muted-foreground">Click or drag to replace</p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={logoBusy}
                onClick={(e) => {
                  e.stopPropagation();
                  onLogoDelete();
                }}
              >
                {isDeletingLogo ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
              </Button>
            </div>
          )}
        </div>
      );
    }
    const logoSrc = formData.logo || company?.logo;
    if (logoSrc) {
      return (
        <div className="flex items-center gap-4">
          <img src={logoSrc} alt="Company Logo" className="h-16 w-auto max-h-16 object-contain border rounded p-1" />
        </div>
      );
    }
    return <p className="text-sm font-medium">-</p>;
  };

  return (
    <TabsContent value="details" className="space-y-6 mt-6 flex-1 overflow-y-auto">
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Company Information</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-muted-foreground">Company ID</Label>
            {isEditing && company?.status === 'pending_setup' ? (
              <Input
                value={formData.companyId}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase().slice(0, 7);
                  if (/^[A-Z0-9]*$/.test(value)) {
                    onFormDataChange({ ...formData, companyId: value });
                  }
                }}
                placeholder="Enter company ID (max 7 alphanumeric)"
                maxLength={7}
              />
            ) : (
              <p className="text-sm font-medium">{company?.code || '-'}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-muted-foreground">Status</Label>
            <div>
              {company?.status === 'active' && <Badge variant="default">Active</Badge>}
              {company?.status === 'pending_setup' && (
                <Badge className="bg-orange-500/10 text-orange-500 hover:bg-orange-500/20">Pending Setup</Badge>
              )}
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-muted-foreground">Company Name</Label>
            {isEditing ? (
              <Input
                value={formData.companyName}
                onChange={(e) => onFormDataChange({ ...formData, companyName: e.target.value })}
                placeholder="Enter company name"
              />
            ) : (
              <p className="text-sm font-medium">{company?.name || '-'}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-muted-foreground">Currency</Label>
            <p className="text-sm font-medium">{company?.currencyCode || '-'}</p>
          </div>
          <div className="space-y-2 col-span-2">
            <Label className="text-muted-foreground">Address</Label>
            {isEditing ? (
              <Textarea
                value={formData.address}
                onChange={(e) => onFormDataChange({ ...formData, address: e.target.value })}
                placeholder="Enter address"
                rows={3}
              />
            ) : (
              <p className="text-sm font-medium">{formData.address || '-'}</p>
            )}
          </div>
          {renderEditableField('VAT Registration Number', 'vatRegistrationNumber', 'Enter VAT registration number')}
          {renderEditableField(
            'Company Registration Number',
            'companyRegistrationNumber',
            'Enter company registration number',
          )}
          {renderEditableField('Phone', 'phone', 'Enter phone number')}
          {renderEditableField('Email', 'email', 'Enter email address', false, 'email')}
          <div className="space-y-2 col-span-2">
            <Label className="text-muted-foreground">Company Logo</Label>
            {renderLogoSection()}
          </div>
        </div>
      </div>

      <div className="space-y-4 pt-4 border-t">
        <h3 className="text-lg font-semibold">Additional Information</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-muted-foreground">User Count</Label>
            <p className="text-sm font-medium">{company?.userCount || 0}</p>
          </div>
          <div className="space-y-2">
            <Label className="text-muted-foreground">Created At</Label>
            <p className="text-sm font-medium">
              {company?.createdAt
                ? new Date(company.createdAt).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                  })
                : '-'}
            </p>
          </div>
        </div>
      </div>
    </TabsContent>
  );
}

interface BankDetailsTabContentProps {
  isEditing: boolean;
  bankDetails: BankDetail[];
  onAddRow: () => void;
  onRemoveRow: (id: string) => void;
  onChange: (id: string, field: keyof BankDetail, value: string) => void;
  currencies: CurrencyApi[];
  currenciesLoading: boolean;
  rowErrors: Record<string, BankDetailFieldErrors>;
  onBlurRow: (id: string) => void;
}

type BankDetailTextField = 'sortCode' | 'accountNumber' | 'accountName' | 'swiftBicCode';

function BankDetailsTabContent({
  isEditing,
  bankDetails,
  onAddRow,
  onRemoveRow,
  onChange,
  currencies,
  currenciesLoading,
  rowErrors,
  onBlurRow,
}: Readonly<BankDetailsTabContentProps>) {
  const renderError = (message?: string) =>
    message ? <p className="mt-1 text-xs text-destructive">{message}</p> : null;

  const renderTextCell = (detail: BankDetail, field: BankDetailTextField, placeholder: string, minWidth: string) => {
    if (!isEditing) {
      return <span>{detail[field] || '-'}</span>;
    }
    const error = rowErrors[detail.id]?.[field];
    return (
      <div>
        <Input
          value={detail[field]}
          onChange={(e) => onChange(detail.id, field, e.target.value)}
          onBlur={() => onBlurRow(detail.id)}
          placeholder={placeholder}
          className={minWidth}
          maxLength={BANK_DETAIL_MAX_LENGTHS[field]}
          aria-invalid={error ? true : undefined}
        />
        {renderError(error)}
      </div>
    );
  };

  const renderIbanCell = (detail: BankDetail) => {
    if (!isEditing) {
      return <span>{detail.ibanNumber ? formatIbanDisplay(detail.ibanNumber) : '-'}</span>;
    }
    const error = rowErrors[detail.id]?.ibanNumber;
    return (
      <div>
        <Input
          value={formatIbanDisplay(detail.ibanNumber)}
          onChange={(e) => onChange(detail.id, 'ibanNumber', e.target.value)}
          onBlur={() => onBlurRow(detail.id)}
          placeholder="GB00 XXXX 0000 0000 0000 00"
          className="min-w-[220px]"
          // Account for spaces inserted by formatIbanDisplay (cap + spaces between 4-char groups)
          maxLength={BANK_DETAIL_MAX_LENGTHS.ibanNumber + Math.ceil(BANK_DETAIL_MAX_LENGTHS.ibanNumber / 4)}
          aria-invalid={error ? true : undefined}
        />
        {renderError(error)}
      </div>
    );
  };

  const renderCurrencyCell = (detail: BankDetail) => {
    if (!isEditing) {
      const match = currencies.find((c) => c.code === detail.currency);
      return <span>{match ? `${match.code}` : detail.currency || '-'}</span>;
    }
    const error = rowErrors[detail.id]?.currency;
    const placeholder = currenciesLoading ? 'Loading…' : 'Select currency';
    return (
      <div>
        <CurrencyCombobox
          currencies={currencies}
          value={detail.currency || undefined}
          onValueChange={(value) => {
            onChange(detail.id, 'currency', value);
            onBlurRow(detail.id);
          }}
          disabled={currenciesLoading}
          placeholder={placeholder}
          invalid={!!error}
          className="min-w-[140px]"
        />
        {renderError(error)}
      </div>
    );
  };

  return (
    <TabsContent value="bank-details" className="space-y-4 mt-6 flex-1 overflow-y-auto">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Bank Account Details</h3>
          {isEditing && (
            <Button type="button" variant="outline" size="sm" onClick={onAddRow}>
              <Plus className="h-4 w-4 mr-2" />
              Add Bank Detail Row
            </Button>
          )}
        </div>
        <div className="border rounded-lg overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Currency</TableHead>
                <TableHead>Sort Code</TableHead>
                <TableHead>Account Number</TableHead>
                <TableHead>Account Name</TableHead>
                <TableHead>IBAN Number</TableHead>
                <TableHead>SWIFT/BIC Code</TableHead>
                {isEditing && <TableHead className="w-[50px]"></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {bankDetails.length > 0 ? (
                bankDetails.map((detail) => (
                  <TableRow key={detail.id}>
                    <TableCell className="align-top">{renderCurrencyCell(detail)}</TableCell>
                    <TableCell className="align-top">
                      {renderTextCell(detail, 'sortCode', '00-00-00', 'min-w-[100px]')}
                    </TableCell>
                    <TableCell className="align-top">
                      {renderTextCell(detail, 'accountNumber', '12345678', 'min-w-[120px]')}
                    </TableCell>
                    <TableCell className="align-top">
                      {renderTextCell(detail, 'accountName', 'Account Name', 'min-w-[150px]')}
                    </TableCell>
                    <TableCell className="align-top">{renderIbanCell(detail)}</TableCell>
                    <TableCell className="align-top">
                      {renderTextCell(detail, 'swiftBicCode', 'XXXXGB00', 'min-w-[120px]')}
                    </TableCell>
                    {isEditing && (
                      <TableCell className="align-top">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => onRemoveRow(detail.id)}
                          disabled={bankDetails.length === 1}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={isEditing ? 7 : 6} className="text-center text-muted-foreground py-8">
                    No bank details available for this company
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        {!isEditing && (
          <p className="text-sm text-muted-foreground">Bank details can be managed through the edit function.</p>
        )}
      </div>
    </TabsContent>
  );
}

interface CompanyViewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  company: Company | null;
  isDemo: boolean;
  apiGet: <T>(url: string) => Promise<T>;
  refetchCompanies: () => Promise<unknown>;
  onSave: (companyId: string, updates: Partial<Company>) => void;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function CompanyViewDialog({
  open,
  onOpenChange,
  company,
  isDemo,
  apiGet: _apiGet,
  refetchCompanies,
  onSave,
}: Readonly<CompanyViewDialogProps>) {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState<EditViewFormData>({
    companyId: '',
    companyName: '',
    status: 'active',
    taxId: '',
    description: '',
    address: '',
    vatRegistrationNumber: '',
    companyRegistrationNumber: '',
    phone: '',
    email: '',
    logo: null,
  });
  const [localBankDetails, setLocalBankDetails] = useState<BankDetail[]>([INITIAL_BANK_DETAIL]);
  const [draggingLogo, setDraggingLogo] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isDeletingLogo, setIsDeletingLogo] = useState(false);

  const queryClient = useQueryClient();
  const { apiPut, apiPostMultipart, apiDelete } = useApi();
  const {
    data: companyData,
    isError: isCompanyDataError,
    error: companyDataError,
    isLoading: isCompanyDataLoading,
    refetch: refetchCompanyData,
  } = useCompanyWithProfile(open ? company?.id : undefined);
  const companyDetails = companyData?.details;
  const bankDetailsData = companyData?.bankDetails;

  const { data: currenciesData, isLoading: currenciesLoading } = useBankDetailsCurrencies(
    company?.linkedEvoSubscriptionId,
    company?.linkedProductId,
  );
  const currencies = useMemo(() => currenciesData ?? [], [currenciesData]);
  const validCurrencyCodes = useMemo(() => new Set(currencies.map((c) => c.code)), [currencies]);

  const [bankRowErrors, setBankRowErrors] = useState<Record<string, BankDetailFieldErrors>>({});
  const [validationErrors, setValidationErrors] = useState<Record<string, string | null>>({});
  const [validationWarnings, setValidationWarnings] = useState<Record<string, string | null>>({});

  useEffect(() => {
    if (company && open) {
      setFormData(buildEditFormFromCompany(company));
      setBankRowErrors({});
      setIsEditing(false);
      setValidationErrors({});
      setValidationWarnings({});
    }
  }, [company, open]);

  const handleFieldChange = useCallback((field: string) => {
    setValidationErrors((prev) => (prev[field] ? { ...prev, [field]: null } : prev));
    setValidationWarnings((prev) => (prev[field] ? { ...prev, [field]: null } : prev));
  }, []);

  const handleFieldBlur = useCallback((field: string, value: string) => {
    if (field === 'email') {
      setValidationErrors((prev) => ({ ...prev, email: validateEmail(value) }));
    } else if (field === 'phone') {
      setValidationErrors((prev) => ({ ...prev, phone: validatePhone(value) }));
    } else if (field === 'vatRegistrationNumber') {
      setValidationWarnings((prev) => ({ ...prev, vatRegistrationNumber: validateUkVat(value) }));
    } else if (field === 'companyRegistrationNumber') {
      setValidationWarnings((prev) => ({ ...prev, companyRegistrationNumber: validateUkCrn(value) }));
    }
  }, []);

  // Reset bank details only when the company ID changes (switching companies or opening the
  // dialog for the first time) — not when company fields update after a save. The second
  // effect below repopulates localBankDetails once bankDetailsData arrives from the API.
  const companyId = company?.id;
  useEffect(() => {
    if (companyId && open) {
      setLocalBankDetails([INITIAL_BANK_DETAIL]);
    }
  }, [companyId, open]);

  useEffect(() => {
    if (bankDetailsData && open) {
      const mapped: BankDetail[] = bankDetailsData.map((row) => ({
        id: String(row.id),
        serverId: row.id,
        currency: row.currency ?? '',
        sortCode: row.sortCode ?? '',
        accountNumber: row.accountNumber ?? '',
        accountName: row.accountName ?? '',
        ibanNumber: stripIbanSpaces(row.ibanNumber ?? '').toUpperCase(),
        swiftBicCode: row.swiftBicCode ?? '',
      }));
      setLocalBankDetails(mapped.length > 0 ? mapped : [INITIAL_BANK_DETAIL]);
      setBankRowErrors({});
    }
  }, [bankDetailsData, open]);

  // Populate details fields once they load from the API
  useEffect(() => {
    if (companyDetails && open) {
      setFormData((prev) => ({
        ...prev,
        address: companyDetails.addressLine ?? '',
        vatRegistrationNumber: companyDetails.vatNumber ?? '',
        companyRegistrationNumber: companyDetails.companyRegistrationNumber ?? '',
        phone: companyDetails.phone ?? '',
        email: companyDetails.email ?? '',
        logo: companyDetails.logoUrl ?? null,
      }));
    }
  }, [companyDetails, open]);

  const handleLogoFile = async (file: File) => {
    if (!file) return;
    const validationError = validateLogoFile(file);
    if (validationError) {
      toast.error(validationError);
      return;
    }
    if (isDemo) {
      const previewUrl = URL.createObjectURL(file);
      setFormData((prev) => ({ ...prev, logo: previewUrl }));
      return;
    }
    if (!company) return;
    const numericCompanyId = Number(company.id);
    setIsUploadingLogo(true);
    try {
      const logoFormData = new FormData();
      logoFormData.append('file', file);
      const updated = await apiPostMultipart<CompanyWithProfileApi>(
        `/api/v1/company/${numericCompanyId}/logo`,
        logoFormData,
      );
      setFormData((prev) => ({ ...prev, logo: updated.details.logoUrl ?? null }));
      queryClient.invalidateQueries({ queryKey: ['company-with-profile', numericCompanyId] });
      toast.success('Logo updated');
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Failed to update logo');
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleLogoDelete = async () => {
    if (isDemo) {
      setFormData((prev) => ({ ...prev, logo: null }));
      return;
    }
    if (!company) return;
    const numericCompanyId = Number(company.id);
    setIsDeletingLogo(true);
    try {
      await apiDelete(`/api/v1/company/${company.id}/logo`);
      setFormData((prev) => ({ ...prev, logo: null }));
      queryClient.invalidateQueries({ queryKey: ['company-with-profile', numericCompanyId] });
      toast.success('Logo removed');
    } catch (error) {
      console.error('Error deleting logo:', error);
      toast.error('Failed to remove logo');
    } finally {
      setIsDeletingLogo(false);
    }
  };

  const handleLogoDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDraggingLogo(true);
  };
  const handleLogoDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDraggingLogo(false);
  };
  const handleLogoDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDraggingLogo(false);
    const file = e.dataTransfer.files[0];
    if (file) handleLogoFile(file);
  };

  const handleAddBankRow = () => {
    const newId = `row-${Date.now()}-${localBankDetails.length + 1}`;
    setLocalBankDetails([
      ...localBankDetails,
      {
        id: newId,
        currency: '',
        sortCode: '',
        accountNumber: '',
        accountName: '',
        ibanNumber: '',
        swiftBicCode: '',
      },
    ]);
  };

  const handleRemoveBankRow = (id: string) => {
    if (localBankDetails.length > 1) {
      setLocalBankDetails(localBankDetails.filter((d) => d.id !== id));
      setBankRowErrors((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  };

  const handleBankChange = (id: string, field: keyof BankDetail, value: string) => {
    // IBAN is stored stripped (raw) so the API sees no spaces — the input
    // displays the formatted version via formatIbanDisplay().
    const normalised = field === 'ibanNumber' ? stripIbanSpaces(value).toUpperCase() : value;
    setLocalBankDetails((prev) => prev.map((d) => (d.id === id ? { ...d, [field]: normalised } : d)));
    // Clear the specific field error while the user is typing; re-validated on blur/save.
    setBankRowErrors((prev) => {
      const current = prev[id];
      if (!current || !(field in current)) return prev;
      const nextForRow = { ...current };
      delete nextForRow[field];
      return { ...prev, [id]: nextForRow };
    });
  };

  const handleBankRowBlur = useCallback(
    (id: string) => {
      const row = localBankDetails.find((r) => r.id === id);
      if (!row) return;
      const errors = validateBankDetailRow(
        {
          currency: row.currency || null,
          sortCode: row.sortCode || null,
          accountNumber: row.accountNumber || null,
          accountName: row.accountName || null,
          ibanNumber: row.ibanNumber || null,
          swiftBicCode: row.swiftBicCode || null,
        },
        validCurrencyCodes,
      );
      setBankRowErrors((e) => ({ ...e, [id]: errors }));
    },
    [localBankDetails, validCurrencyCodes],
  );

  // Recompute all row errors; returns true when every row is valid.
  const validateAllBankRows = useCallback((): boolean => {
    const next: Record<string, BankDetailFieldErrors> = {};
    let allValid = true;
    for (const row of localBankDetails) {
      const errors = validateBankDetailRow(
        {
          currency: row.currency || null,
          sortCode: row.sortCode || null,
          accountNumber: row.accountNumber || null,
          accountName: row.accountName || null,
          ibanNumber: row.ibanNumber || null,
          swiftBicCode: row.swiftBicCode || null,
        },
        validCurrencyCodes,
      );
      next[row.id] = errors;
      if (hasBankDetailErrors(errors)) allValid = false;
    }
    setBankRowErrors(next);
    return allValid;
  }, [localBankDetails, validCurrencyCodes]);

  // Disable save while any row has known errors (derived, no extra state).
  const hasAnyBankErrors = useMemo(() => Object.values(bankRowErrors).some(hasBankDetailErrors), [bankRowErrors]);

  const handleSave = async () => {
    if (!company || isSaving) return;

    const emailError = validateEmail(formData.email);
    const phoneError = validatePhone(formData.phone);
    if (emailError || phoneError) {
      setValidationErrors({
        email: emailError,
        phone: phoneError,
      });
      toast.error('Please fix the errors before saving');
      return;
    }
    setValidationErrors({});

    const vatWarning = validateUkVat(formData.vatRegistrationNumber);
    const crnWarning = validateUkCrn(formData.companyRegistrationNumber);
    setValidationWarnings({
      vatRegistrationNumber: vatWarning,
      companyRegistrationNumber: crnWarning,
    });

    if (!validateAllBankRows()) {
      toast.error('Please fix the highlighted bank details before saving');
      return;
    }

    setIsSaving(true);
    try {
      if (!isDemo) {
        const numericCompanyId = Number(company.id);
        await apiPut(`/api/v1/company/${company.id}`, {
          name: formData.companyName,
          status: formData.status,
          code: formData.companyId || null,
          taxId: formData.taxId || null,
          externalCompanyCode: company.linkedSubLedger || null,
          externalCompanyName: company.externalCompanyName || null,
          details: {
            addressLine: formData.address || null,
            country: null,
            vatNumber: formData.vatRegistrationNumber || null,
            companyRegistrationNumber: formData.companyRegistrationNumber || null,
            phone: formData.phone || null,
            email: formData.email || null,
          },
          bankDetails: localBankDetails
            .filter(
              (r) => r.currency || r.sortCode || r.accountNumber || r.accountName || r.ibanNumber || r.swiftBicCode,
            )
            .map((row) => ({
              currency: row.currency || null,
              sortCode: row.sortCode || null,
              accountNumber: row.accountNumber || null,
              accountName: row.accountName || null,
              ibanNumber: row.ibanNumber ? stripIbanSpaces(row.ibanNumber) : null,
              swiftBicCode: row.swiftBicCode || null,
            })),
        });

        queryClient.invalidateQueries({ queryKey: ['company-with-profile', numericCompanyId] });
        await refetchCompanies();
        onSave(company.id, {
          code: formData.companyId,
          name: formData.companyName,
          status: formData.status,
          taxId: formData.taxId,
          description: formData.description,
          address: formData.address,
          vatRegistrationNumber: formData.vatRegistrationNumber,
          companyRegistrationNumber: formData.companyRegistrationNumber,
          phone: formData.phone,
          email: formData.email,
          linkedEvoSubscriptionId: company.linkedEvoSubscriptionId,
          linkedSubscriptionName: company.linkedSubscriptionName,
          linkedProductId: company.linkedProductId,
          linkedProductName: company.linkedProductName,
          linkedSubLedger: company.linkedSubLedger,
          integrationStatus: company.integrationStatus,
        });
      } else {
        onSave(company.id, {
          code: formData.companyId,
          name: formData.companyName,
          status: formData.status,
          taxId: formData.taxId,
          description: formData.description,
          address: formData.address,
          vatRegistrationNumber: formData.vatRegistrationNumber,
          companyRegistrationNumber: formData.companyRegistrationNumber,
          phone: formData.phone,
          email: formData.email,
          logo: formData.logo || company.logo,
          linkedEvoSubscriptionId: company.linkedEvoSubscriptionId,
          linkedSubscriptionName: company.linkedSubscriptionName,
          linkedProductId: company.linkedProductId,
          linkedProductName: company.linkedProductName,
          linkedSubLedger: company.linkedSubLedger,
          integrationStatus: company.integrationStatus,
        });
      }
      setIsEditing(false);
      setValidationWarnings({});
      toast.success('Company details updated successfully');
    } catch (error) {
      console.error('Error updating company:', error);
      const message = error instanceof ApiError ? error.message : 'Failed to update company details';
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setBankRowErrors({});
    setValidationErrors({});
    setValidationWarnings({});
    if (company) setFormData(buildEditFormFromCompany(company));
  };

  const handleOpenChange = (isOpen: boolean) => {
    onOpenChange(isOpen);
    if (!isOpen) setIsEditing(false);
  };

  // If the company details fetch fails, exit edit mode — we can't safely edit
  // a record we couldn't load.
  useEffect(() => {
    if (isCompanyDataError) setIsEditing(false);
  }, [isCompanyDataError]);

  const loadErrorMessage =
    companyDataError instanceof ApiError
      ? companyDataError.message
      : companyDataError?.message || 'An unexpected error occurred while loading company details.';

  const renderDialogFooterButtons = () => {
    if (isCompanyDataError) {
      return (
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          Close
        </Button>
      );
    }
    if (isEditing) {
      return (
        <>
          <Button variant="outline" onClick={handleCancelEdit}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={hasAnyBankErrors || !!validationErrors.email || !!validationErrors.phone || isSaving}
          >
            {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save Changes
          </Button>
        </>
      );
    }
    return (
      <>
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          Close
        </Button>
        <Button onClick={() => setIsEditing(true)}>Edit</Button>
      </>
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-6xl h-[85vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Company Details</DialogTitle>
          <DialogDescription>View all information for {company?.name}</DialogDescription>
        </DialogHeader>
        {isCompanyDataError ? (
          <div className="flex-1 flex items-center justify-center overflow-y-auto">
            <Alert variant="destructive" className="max-w-lg">
              <AlertTitle>Unable to load company details</AlertTitle>
              <AlertDescription className="space-y-3">
                <p>{loadErrorMessage}</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => refetchCompanyData()}
                  disabled={isCompanyDataLoading}
                >
                  {isCompanyDataLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Retrying…
                    </>
                  ) : (
                    'Try again'
                  )}
                </Button>
              </AlertDescription>
            </Alert>
          </div>
        ) : (
          <Tabs defaultValue="details" className="w-full flex-1 flex flex-col overflow-hidden">
            <TabsList className="grid w-full grid-cols-3 flex-shrink-0">
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="integration">Integration</TabsTrigger>
              <TabsTrigger value="bank-details">Bank Details</TabsTrigger>
            </TabsList>

            <DetailsTabContent
              company={company}
              isEditing={isEditing}
              formData={formData}
              onFormDataChange={setFormData}
              draggingLogo={draggingLogo}
              onLogoDragOver={handleLogoDragOver}
              onLogoDragLeave={handleLogoDragLeave}
              onLogoDrop={handleLogoDrop}
              onLogoFile={handleLogoFile}
              onLogoDelete={handleLogoDelete}
              isUploadingLogo={isUploadingLogo}
              isDeletingLogo={isDeletingLogo}
              validationErrors={validationErrors}
              validationWarnings={validationWarnings}
              onFieldChange={handleFieldChange}
              onFieldBlur={handleFieldBlur}
            />

            <TabsContent value="integration" className="space-y-6 mt-6 flex-1 overflow-y-auto">
              {isEditing && (
                <Alert>
                  <AlertDescription>
                    Integration settings cannot be changed after a company has been created. To update the integration,
                    please contact your administrator.
                  </AlertDescription>
                </Alert>
              )}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Integration Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Integration Status</Label>
                    <div>
                      <Badge
                        variant={getIntegrationBadgeVariant(company?.integrationStatus)}
                        className={
                          company?.integrationStatus === 'none' && company?.linkedEvoSubscriptionId
                            ? 'bg-orange-500/10 text-orange-500'
                            : ''
                        }
                      >
                        {company?.integrationStatus === 'connected' && '✓ Connected'}
                        {company?.integrationStatus === 'partial' && '⚠ Partial'}
                        {company?.integrationStatus === 'none' &&
                          company?.linkedEvoSubscriptionId &&
                          '⏳ Pending Setup'}
                        {company?.integrationStatus === 'none' && !company?.linkedEvoSubscriptionId && 'Not Connected'}
                      </Badge>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Product</Label>
                    <p className="text-sm font-medium">{company?.linkedProductName || '-'}</p>
                  </div>
                  {company?.linkedProductName && (
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">Instance</Label>
                      <p className="text-sm font-medium">{company?.linkedSubscriptionName || '-'}</p>
                    </div>
                  )}
                  {company?.linkedEvoSubscriptionId && (
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">Sub Ledger</Label>
                      <p className="text-sm font-medium">{company?.linkedSubLedger || '-'}</p>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Subscription ID</Label>
                    <p className="text-sm font-medium break-all">{company?.linkedEvoSubscriptionId || '-'}</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Last Sync</Label>
                    <p className="text-sm font-medium">
                      {company?.lastSyncedAt ? formatCompanyDateTime(company.lastSyncedAt) : '—'}
                    </p>
                  </div>
                </div>
              </div>
            </TabsContent>

            <BankDetailsTabContent
              isEditing={isEditing}
              bankDetails={localBankDetails}
              onAddRow={handleAddBankRow}
              onRemoveRow={handleRemoveBankRow}
              onChange={handleBankChange}
              currencies={currencies}
              currenciesLoading={currenciesLoading}
              rowErrors={bankRowErrors}
              onBlurRow={handleBankRowBlur}
            />
          </Tabs>
        )}
        <DialogFooter className="mt-6 flex-shrink-0">{renderDialogFooterButtons()}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface CompanySettingsPageProps {
  isOnboarding?: boolean;
  onCompanyAdded?: () => void;
}

export function CompanySettingsPage({ isOnboarding = false, onCompanyAdded }: CompanySettingsPageProps = {}) {
  // Defence-in-depth: ProtectedAdminRoute already gates this route at the router level.
  // This check is a belt-and-braces fallback for stale-cache / race-condition edge cases.
  const { isAdmin, isLoading: isAdminLoading } = useIsAdmin();

  const navigate = useNavigate();
  const { apiGet, apiPost, apiPut, apiPostMultipart } = useApi();
  const queryClient = useQueryClient();
  void queryClient; // Available for future cache invalidation
  const { isDemo } = useMode();
  const { canForceSync } = useScreenModes();
  const { refreshExternalCompanies } = useCompany();

  // Fetch companies using mode-aware query (demo mode uses mock data, live mode uses API)
  const { data: fetchedCompanies, refetch: refetchCompanies, isLoading: isCompaniesLoading } = useCompaniesList();

  // Local state for companies (initialized from fetched data, can be modified locally)
  const [companies, setCompanies] = useState<Company[]>([]);
  // Tracks whether the manage-integrations dialog was opened as part of a new company creation
  const [isNewCompanyFlow, setIsNewCompanyFlow] = useState(false);
  const [editingPendingCompanyId, setEditingPendingCompanyId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sortOption, setSortOption] = useState<SortOption>('name-asc');

  // Update local companies state when fetched data changes
  useEffect(() => {
    if (fetchedCompanies) {
      setCompanies(mapFetchedToCompanies(fetchedCompanies));
    }
  }, [fetchedCompanies, isOnboarding]);

  // Dialog states
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isManageIntegrationsDialogOpen, setIsManageIntegrationsDialogOpen] = useState(false);
  const [isManageBankDetailsDialogOpen, setIsManageBankDetailsDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [viewCompanyData, setViewCompanyData] = useState<Company | null>(null);

  // Form states
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [formData, setFormData] = useState({
    companyId: '',
    name: '',
    address: '',
    vatRegistrationNo: '',
    companyRegistrationNo: '',
    phone: '',
    email: '',
    status: 'active' as 'active' | 'pending_setup',
    logo: '',
    description: '',
    currencyCode: undefined as string | undefined,
    code: '',
    taxId: '',
  });

  // Logo upload states
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [isDraggingLogo, setIsDraggingLogo] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);

  // Instances state
  const [instances, setInstances] = useState<Instance[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<string>('');
  const [selectedInstanceId, setSelectedInstanceId] = useState<string>('');
  const [isLoadingInstances, setIsLoadingInstances] = useState(false);
  const [instancesError, setInstancesError] = useState<string | null>(null);

  // Product availability (probed when the Manage Integrations dialog opens)
  const [productAvailability, setProductAvailability] = useState<AvailableProducts>({
    Financials: 'loading',
    DimensionsvNext: 'loading',
  });

  // Sub Ledgers state
  const [subLedgers, setSubLedgers] = useState<SubLedgerOption[]>([]);
  const [selectedSubLedger, setSelectedSubLedger] = useState<string>('');
  const [isLoadingSubLedgers, setIsLoadingSubLedgers] = useState(false);
  const [subLedgersError, setSubLedgersError] = useState<string | null>(null);
  const [generatePerSubLedger, setGeneratePerSubLedger] = useState(false);
  const [isAddMode, setIsAddMode] = useState(false); // Track if we're in "add new company" mode
  const [instancePickerOpen, setInstancePickerOpen] = useState(false);
  const [subLedgerPickerOpen, setSubLedgerPickerOpen] = useState(false);

  // Bank Details state
  const [isSavingIntegration, setIsSavingIntegration] = useState(false);
  const [createValidationErrors, setCreateValidationErrors] = useState<Record<string, string | null>>({});
  const [createValidationWarnings, setCreateValidationWarnings] = useState<Record<string, string | null>>({});
  const [bankDetails, setBankDetails] = useState<BankDetail[]>([
    { id: '1', currency: '', sortCode: '', accountNumber: '', accountName: '', ibanNumber: '', swiftBicCode: '' },
  ]);

  // Currency list + per-row validation errors for the integration/create-company bank details flow.
  // When completing a pending company's setup, selectedInstanceId/selectedProduct aren't set (the user
  // skips the Manage Integrations step), so fall back to the company's already-stored linked IDs.
  const currenciesSubscriptionId = selectedInstanceId || selectedCompany?.linkedEvoSubscriptionId || null;
  const currenciesProductId = getProductId(selectedProduct) ?? selectedCompany?.linkedProductId ?? null;
  const { data: integrationCurrenciesData, isLoading: integrationCurrenciesLoading } = useBankDetailsCurrencies(
    currenciesSubscriptionId,
    currenciesProductId,
  );
  const integrationCurrencies = useMemo(() => integrationCurrenciesData ?? [], [integrationCurrenciesData]);
  const integrationValidCurrencyCodes = useMemo(
    () => new Set(integrationCurrencies.map((c) => c.code)),
    [integrationCurrencies],
  );
  const [integrationBankRowErrors, setIntegrationBankRowErrors] = useState<Record<string, BankDetailFieldErrors>>({});

  // Fetch summary counts from API (real counts, not derived from the list)
  const { data: summary } = useCompanySummary();

  // Filter and sort companies, then group sub-companies under their multi-company parent
  const filteredAndSortedCompanies = useMemo(
    () => buildFilteredAndSortedCompanies(companies, searchTerm, statusFilter, sortOption),
    [companies, searchTerm, statusFilter, sortOption],
  );

  const alreadyUsedSubLedgerCodes = useMemo(() => {
    const used = new Set<string>();
    for (const c of companies) {
      if (c.linkedEvoSubscriptionId === selectedInstanceId && c.id !== selectedCompany?.id && c.linkedSubLedger) {
        used.add(c.linkedSubLedger);
      }
    }
    return used;
  }, [companies, selectedInstanceId, selectedCompany?.id]);

  // Logo drag & drop handlers
  const handleLogoDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingLogo(true);
  }, []);

  const handleLogoDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingLogo(false);
  }, []);

  const handleLogoDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleLogoFileSelection = useCallback((file: File) => {
    const error = validateLogoFile(file);
    if (error) {
      toast.error(error);
      return;
    }

    setLogoFile(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  }, []);

  const handleLogoDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDraggingLogo(false);

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        handleLogoFileSelection(files[0]);
      }
    },
    [handleLogoFileSelection],
  );

  const handleLogoInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleLogoFileSelection(file);
  };

  const removeLogoFile = () => {
    setLogoFile(null);
    setLogoPreview('');
    if (logoInputRef.current) {
      logoInputRef.current.value = '';
    }
  };

  const validateCompanyId = (value: string): boolean => {
    // Only allow alphanumeric characters, max 7 characters
    const regex = /^[a-zA-Z0-9]{0,7}$/;
    return regex.test(value);
  };

  const handleAddCompany = () => {
    if (!formData.companyId.trim()) {
      toast.error('Company ID is required');
      return;
    }
    if (!formData.name.trim()) {
      toast.error('Company name is required');
      return;
    }
    // Don't create the company yet — move to integration step first
    const pendingCompany: Company = {
      id: '',
      name: formData.name,
      status: formData.status,
      logo: logoPreview || undefined,
      description: formData.description || undefined,
      integrationStatus: 'none',
      userCount: 0,
      createdAt: new Date().toISOString(),
      code: formData.companyId,
      currencyCode: formData.currencyCode,
      taxId: formData.taxId,
    };
    setIsAddDialogOpen(false);
    setSelectedCompany(pendingCompany);
    setSelectedProduct('');
    setSelectedInstanceId('');
    setInstances([]);
    setIsNewCompanyFlow(true);
    setIsManageIntegrationsDialogOpen(true);
  };

  const handleEditCompany = () => {
    if (!selectedCompany) return;
    editCompanyAction(selectedCompany, formData, isDemo, apiPut, refetchCompanies, setCompanies, () => {
      setIsEditDialogOpen(false);
      setSelectedCompany(null);
      setFormData(INITIAL_FORM_DATA);
    });
  };

  const handleViewCompany = (company: Company) => {
    setViewCompanyData(company);
    setIsViewDialogOpen(true);
  };

  const handleCompletePendingSetup = (company: Company) => {
    setFormData({
      ...INITIAL_FORM_DATA,
      companyId: company.code || '',
      name: company.name,
      taxId: company.taxId || '',
      currencyCode: company.currencyCode,
    });
    setEditingPendingCompanyId(company.id);
    setIsNewCompanyFlow(true);
    setIsAddDialogOpen(true);
  };

  const handleManageIntegrations = (company: Company) => {
    setSelectedCompany(company);
    setSelectedProduct(company.linkedProductName || '');
    setSelectedInstanceId(company.linkedEvoSubscriptionId || '');
    setInstances([]);
    setSubLedgers([]);
    setSelectedSubLedger('');
    setGeneratePerSubLedger(false);
    setIsManageIntegrationsDialogOpen(true);
  };
  const handleViewDialogSave = (companyId: string, updates: Partial<Company>) => {
    setCompanies((prev) => prev.map((c) => (c.id === companyId ? { ...c, ...updates } : c)));
    setViewCompanyData((prev) => (prev ? { ...prev, ...updates } : prev));
  };

  // Probe product availability whenever the Manage Integrations dialog opens
  useEffect(() => {
    if (!isManageIntegrationsDialogOpen) return;

    setProductAvailability({ Financials: 'loading', DimensionsvNext: 'loading' });

    probeAvailableProducts(isDemo, apiGet).then(({ Financials, DimensionsvNext }) => {
      const availability: AvailableProducts = {
        Financials: Financials ? 'available' : 'unavailable',
        DimensionsvNext: DimensionsvNext ? 'available' : 'unavailable',
      };
      setProductAvailability(availability);

      // Auto-select if exactly one product is available
      let onlyProduct: string | null = null;
      if (Financials && !DimensionsvNext) {
        onlyProduct = 'Financials';
      } else if (!Financials && DimensionsvNext) {
        onlyProduct = 'DimensionsvNext';
      }

      if (onlyProduct && !selectedProduct) {
        setSelectedProduct(onlyProduct);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isManageIntegrationsDialogOpen]);

  useEffect(() => {
    fetchInstancesForProduct(selectedProduct, isDemo, apiGet, {
      setInstances,
      setInstanceId: setSelectedInstanceId,
      setLoading: setIsLoadingInstances,
      setError: setInstancesError,
    });
  }, [selectedProduct, apiGet, isDemo]);

  useEffect(() => {
    fetchSubLedgersForInstance(selectedInstanceId, selectedProduct, isDemo, apiGet, {
      setSubLedgers: (data) => {
        setSubLedgers(data);
        const usedCodes = new Set(
          companies.flatMap((c) =>
            c.linkedEvoSubscriptionId === selectedInstanceId && c.id !== selectedCompany?.id && c.linkedSubLedger
              ? [c.linkedSubLedger]
              : [],
          ),
        );
        const firstAvailable = data.find((sl) => !usedCodes.has(sl.code));
        if (data.length > 0) {
          setGeneratePerSubLedger(true);
          setSelectedSubLedger(firstAvailable ? firstAvailable.code : '');
        }
      },
      setLoading: setIsLoadingSubLedgers,
      setError: setSubLedgersError,
    });
  }, [selectedInstanceId, selectedProduct, apiGet, isDemo]);

  const handleNextToIntegration = () => {
    // Close Manage Integrations dialog
    setIsManageIntegrationsDialogOpen(false);

    // If in add mode, open Add Company dialog; otherwise open Bank Details dialog
    if (isAddMode) {
      const hasSubLedger = selectedSubLedger !== '' && selectedSubLedger !== SUBLEDGER_NONE_VALUE;
      const defaultName = hasSubLedger
        ? subLedgers.find((x) => x.code === selectedSubLedger)?.name || selectedSubLedger
        : instances.find((i) => i.instanceId === selectedInstanceId)?.name || '';
      if (defaultName) {
        setFormData((prev) => ({ ...prev, name: defaultName }));
      }
      setIsAddDialogOpen(true);
    } else {
      setIsManageBankDetailsDialogOpen(true);
    }
  };

  const resetIntegrationState = () => {
    if (isNewCompanyFlow) {
      setIsNewCompanyFlow(false);
      void refreshExternalCompanies();
    }
    setIsManageBankDetailsDialogOpen(false);
    setSelectedCompany(null);
    setLogoFile(null);
    setLogoPreview('');
    setSelectedProduct('');
    setSelectedInstanceId('');
    setSubLedgers([]);
    setSelectedSubLedger('');
    setGeneratePerSubLedger(false);
    setBankDetails([INITIAL_BANK_DETAIL]);
    setIntegrationBankRowErrors({});
    setIsAddMode(false);
    setEditingPendingCompanyId(null);
    setProductAvailability({ Financials: 'loading', DimensionsvNext: 'loading' });
    setCreateValidationErrors({});
    setCreateValidationWarnings({});
  };

  const handleCreateFieldBlur = (
    field: 'email' | 'phone' | 'vatRegistrationNo' | 'companyRegistrationNo',
    value: string,
  ) => {
    if (field === 'email') {
      setCreateValidationErrors((prev) => ({ ...prev, email: validateEmail(value) }));
    } else if (field === 'phone') {
      setCreateValidationErrors((prev) => ({ ...prev, phone: validatePhone(value) }));
    } else if (field === 'vatRegistrationNo') {
      setCreateValidationWarnings((prev) => ({ ...prev, vatRegistrationNo: validateUkVat(value) }));
    } else if (field === 'companyRegistrationNo') {
      setCreateValidationWarnings((prev) => ({ ...prev, companyRegistrationNo: validateUkCrn(value) }));
    }
  };

  const completePendingSetupLive = async (company: Company, pendingCompanyId: string) => {
    await apiPut(`/api/v1/company/${pendingCompanyId}`, {
      name: company.name,
      status: 'active',
      code: company.code || null,
      taxId: company.taxId || null,
      externalCompanyCode: company.linkedSubLedger || null,
      externalCompanyName: company.externalCompanyName || null,
      details: buildDetailsPayload(formData),
      bankDetails: buildBankDetailsPayload(bankDetails),
    });
    if (logoFile) {
      try {
        const logoFormData = new FormData();
        logoFormData.append('file', logoFile);
        const updatedCompany = await apiPostMultipart<CompanyWithProfileApi>(
          `/api/v1/company/${pendingCompanyId}/logo`,
          logoFormData,
        );
        queryClient.setQueryData(['company-with-profile', Number(pendingCompanyId)], updatedCompany);
      } catch (logoError) {
        console.error('Error uploading logo after company activation:', logoError);
        toast.warning('Company activated but logo upload failed');
      }
    }
    await refetchCompanies();
  };

  const uploadLogoForCompany = async (companyId: string | number, context: string) => {
    if (!logoFile) return;
    try {
      const logoFormData = new FormData();
      logoFormData.append('file', logoFile);
      const updatedCompany = await apiPostMultipart<CompanyWithProfileApi>(
        `/api/v1/company/${companyId}/logo`,
        logoFormData,
      );
      queryClient.setQueryData(['company-with-profile', Number(companyId)], updatedCompany);
    } catch (logoError) {
      console.error(`Error uploading logo after company ${context}:`, logoError);
      toast.warning(`Company ${context} but logo upload failed`);
    }
  };

  const updateExistingPendingCompany = async (
    company: Company,
    existingPending: Company,
    bankDetailsPayload: ReturnType<typeof buildBankDetailsPayload>,
    detailsPayload: ReturnType<typeof buildDetailsPayload>,
  ) => {
    await apiPut(`/api/v1/company/${existingPending.id}`, {
      name: company.name,
      status: 'active',
      code: company.code || null,
      taxId: company.taxId || null,
      externalCompanyCode: selectedSubLedger,
      externalCompanyName: subLedgers.find((x) => x.code === selectedSubLedger)?.name ?? selectedSubLedger,
      details: detailsPayload,
      bankDetails: bankDetailsPayload,
    });
    await uploadLogoForCompany(existingPending.id, 'updated');
  };

  const createBrandNewCompanyLive = async (
    company: Company,
    hasSubLedger: boolean,
    bankDetailsPayload: ReturnType<typeof buildBankDetailsPayload>,
    detailsPayload: ReturnType<typeof buildDetailsPayload>,
  ) => {
    const selectedInstance = instances.find((i) => i.instanceId === selectedInstanceId);
    const instanceName = selectedInstance?.name || undefined;
    const productId = getProductId(selectedProduct);
    const createdRaw = await apiPost<CompanyApi | CompanyApi[]>('/api/v1/company', {
      name: company.name,
      isMultiCompany: generatePerSubLedger,
      code: company.code || null,
      taxId: company.taxId || null,
      linkedSubscriptionId: selectedInstanceId || null,
      linkedSubscriptionName: instanceName || null,
      linkedProductId: selectedInstanceId && productId ? productId : null,
      linkedProductName: selectedInstanceId && selectedProduct ? selectedProduct : null,
      linkedDatabaseName: selectedInstance?.databaseName || null,
      datasetId: selectedInstance?.datasetId || null,
      externalCompanyCode: hasSubLedger ? selectedSubLedger : null,
      externalCompanyName: hasSubLedger
        ? subLedgers.find((x) => x.code === selectedSubLedger)?.name || selectedSubLedger
        : null,
      details: detailsPayload,
      bankDetails: bankDetailsPayload,
    });
    const createdList = Array.isArray(createdRaw) ? createdRaw : [createdRaw];
    const logoTarget = createdList.find((c) => c.externalCompanyCode === selectedSubLedger) ?? createdList[0];
    if (logoTarget) {
      await uploadLogoForCompany(logoTarget.id, 'created');
    }
  };

  const createNewCompanyLive = async (company: Company) => {
    const hasSubLedger = selectedSubLedger !== '' && selectedSubLedger !== SUBLEDGER_NONE_VALUE;
    const existingPending = hasSubLedger
      ? companies.find(
          (c) =>
            c.status === 'pending_setup' &&
            c.linkedSubLedger === selectedSubLedger &&
            c.linkedEvoSubscriptionId === selectedInstanceId,
        )
      : undefined;
    const bankDetailsPayload = buildBankDetailsPayload(bankDetails);
    const detailsPayload = buildDetailsPayload(formData);

    if (existingPending) {
      await updateExistingPendingCompany(company, existingPending, bankDetailsPayload, detailsPayload);
    } else {
      await createBrandNewCompanyLive(company, hasSubLedger, bankDetailsPayload, detailsPayload);
    }
    await refetchCompanies();
  };

  const completePendingSetupFlow = async (company: Company, pendingCompanyId: string) => {
    try {
      if (isDemo) {
        await new Promise((resolve) => setTimeout(resolve, 300));
        setCompanies((prev) =>
          prev.map((c) =>
            c.id === pendingCompanyId
              ? { ...c, name: company.name, code: company.code, taxId: company.taxId, status: 'active' }
              : c,
          ),
        );
      } else {
        await completePendingSetupLive(company, pendingCompanyId);
      }
      toast.success('Company setup completed successfully');
      setFormData(INITIAL_FORM_DATA);
      resetIntegrationState();
    } catch (error) {
      console.error('Error completing company setup:', error);
      const message = error instanceof ApiError ? error.message : 'Failed to complete company setup';
      toast.error(message);
    }
  };

  const createNewCompanyFlow = async (company: Company) => {
    const selectedInstance = instances.find((i) => i.instanceId === selectedInstanceId);
    const instanceName = selectedInstance?.name || undefined;
    const productId = getProductId(selectedProduct);
    try {
      if (isDemo) {
        await new Promise((resolve) => setTimeout(resolve, 300));
        const hasSubLedgerDemo = selectedSubLedger !== '' && selectedSubLedger !== SUBLEDGER_NONE_VALUE;
        const mockNewCompany: Company = {
          id: String(Date.now()),
          name: company.name,
          status: company.status,
          logo: company.logo,
          description: company.description,
          integrationStatus: selectedInstanceId ? 'connected' : 'none',
          userCount: 0,
          createdAt: new Date().toISOString(),
          code: company.code,
          currencyCode: company.currencyCode,
          taxId: company.taxId,
          linkedEvoSubscriptionId: selectedInstanceId || undefined,
          linkedSubscriptionName: instanceName,
          linkedProductId: productId,
          linkedProductName: selectedProduct || undefined,
          linkedSubLedger: hasSubLedgerDemo ? selectedSubLedger : undefined,
        };
        setCompanies((prev) => [...prev, mockNewCompany]);
      } else {
        await createNewCompanyLive(company);
      }
      toast.success('Company created successfully');
      onCompanyAdded?.();
      setFormData(INITIAL_FORM_DATA);
      resetIntegrationState();
    } catch (error) {
      console.error('Error creating company:', error);
      const message = error instanceof ApiError ? error.message : 'Failed to create company';
      toast.error(message);
    }
  };

  const handleSaveIntegration = async () => {
    if (!selectedCompany) return;

    const emailError = validateEmail(formData.email);
    const phoneError = validatePhone(formData.phone);
    if (emailError || phoneError) {
      setCreateValidationErrors({ email: emailError, phone: phoneError });
      toast.error('Please fix the errors before saving');
      return;
    }
    setCreateValidationErrors({});

    setCreateValidationWarnings({
      vatRegistrationNo: validateUkVat(formData.vatRegistrationNo),
      companyRegistrationNo: validateUkCrn(formData.companyRegistrationNo),
    });

    if (!validateAllIntegrationBankRows()) {
      toast.error('Please fix the errors in bank details before saving');
      return;
    }

    setIsSavingIntegration(true);
    try {
      if (isNewCompanyFlow && editingPendingCompanyId) {
        await completePendingSetupFlow(selectedCompany, editingPendingCompanyId);
        return;
      }

      if (isNewCompanyFlow) {
        await createNewCompanyFlow(selectedCompany);
      } else {
        await saveIntegrationAction({
          selectedCompany,
          selectedInstanceId,
          selectedProduct,
          selectedSubLedger,
          subLedgers,
          instances,
          isDemo,
          apiPut,
          refetchCompanies,
          updateLocal: setCompanies,
          onDone: resetIntegrationState,
        });
      }
    } finally {
      setIsSavingIntegration(false);
    }
  };

  const handleCancelIntegration = () => {
    if (isNewCompanyFlow) {
      setIsNewCompanyFlow(false);
      setFormData(INITIAL_FORM_DATA);
      toast.info('Company creation cancelled');
    }
    setIsManageIntegrationsDialogOpen(false);
    setSelectedCompany(null);
    setSelectedProduct('');
    setSelectedInstanceId('');
    setSubLedgers([]);
    setSelectedSubLedger('');
    setGeneratePerSubLedger(false);
    setIsAddMode(false);
    setEditingPendingCompanyId(null);
    setFormData(INITIAL_FORM_DATA);
    setLogoFile(null);
    setLogoPreview('');
    setProductAvailability({ Financials: 'loading', DimensionsvNext: 'loading' });
  };

  // Bank Details handlers
  const handleAddBankDetailRow = () => {
    const newId = String(Date.now());
    setBankDetails([
      ...bankDetails,
      {
        id: newId,
        currency: '',
        sortCode: '',
        accountNumber: '',
        accountName: '',
        ibanNumber: '',
        swiftBicCode: '',
      },
    ]);
  };

  const handleRemoveBankDetailRow = (id: string) => {
    if (bankDetails.length > 1) {
      setBankDetails(bankDetails.filter((detail) => detail.id !== id));
      setIntegrationBankRowErrors((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  };

  const handleBankDetailChange = (id: string, field: keyof BankDetail, value: string) => {
    // IBAN stored stripped-and-uppercased; currency uppercased defensively
    // (the dropdown already supplies uppercase ISO codes). Other fields unchanged.
    let normalised = value;
    if (field === 'ibanNumber') {
      normalised = stripIbanSpaces(value).toUpperCase();
    } else if (field === 'currency') {
      normalised = value.toUpperCase();
    }
    setBankDetails(bankDetails.map((detail) => (detail.id === id ? { ...detail, [field]: normalised } : detail)));
    // Clear the specific field error while the user types; re-validated on blur/save.
    setIntegrationBankRowErrors((prev) => {
      const current = prev[id];
      if (!current || !(field in current)) return prev;
      const nextForRow = { ...current };
      delete nextForRow[field];
      return { ...prev, [id]: nextForRow };
    });
  };

  const handleIntegrationBankRowBlur = useCallback(
    (id: string) => {
      const row = bankDetails.find((r) => r.id === id);
      if (!row) return;
      const errors = validateBankDetailRow(
        {
          currency: row.currency || null,
          sortCode: row.sortCode || null,
          accountNumber: row.accountNumber || null,
          accountName: row.accountName || null,
          ibanNumber: row.ibanNumber || null,
          swiftBicCode: row.swiftBicCode || null,
        },
        integrationValidCurrencyCodes,
      );
      setIntegrationBankRowErrors((e) => ({ ...e, [id]: errors }));
    },
    [bankDetails, integrationValidCurrencyCodes],
  );

  // Recompute errors across every non-empty row. Returns true when all rows are valid.
  const validateAllIntegrationBankRows = useCallback((): boolean => {
    const next: Record<string, BankDetailFieldErrors> = {};
    let allValid = true;
    for (const row of bankDetails) {
      const isNonEmpty =
        !!row.currency ||
        !!row.sortCode ||
        !!row.accountNumber ||
        !!row.accountName ||
        !!row.ibanNumber ||
        !!row.swiftBicCode;
      if (!isNonEmpty) continue;
      const errors = validateBankDetailRow(
        {
          currency: row.currency || null,
          sortCode: row.sortCode || null,
          accountNumber: row.accountNumber || null,
          accountName: row.accountName || null,
          ibanNumber: row.ibanNumber || null,
          swiftBicCode: row.swiftBicCode || null,
        },
        integrationValidCurrencyCodes,
      );
      next[row.id] = errors;
      if (hasBankDetailErrors(errors)) allValid = false;
    }
    setIntegrationBankRowErrors(next);
    return allValid;
  }, [bankDetails, integrationValidCurrencyCodes]);

  const hasIntegrationBankErrors = useMemo(
    () => Object.values(integrationBankRowErrors).some(hasBankDetailErrors),
    [integrationBankRowErrors],
  );

  const renderInstancePicker = () => {
    if (isLoadingInstances) {
      return <div className="text-sm text-muted-foreground">Loading instances...</div>;
    }
    if (instancesError) {
      return <div className="text-sm text-destructive">{instancesError}</div>;
    }
    const selectedInstance = instances.find((i) => i.instanceId === selectedInstanceId);
    return (
      <Popover open={instancePickerOpen} onOpenChange={setInstancePickerOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            aria-expanded={instancePickerOpen}
            aria-haspopup="listbox"
            disabled={isLoadingSubLedgers}
            className="w-full justify-between h-9 font-normal"
          >
            <span className={cn('truncate', !selectedInstance && 'text-muted-foreground')}>
              {selectedInstance ? selectedInstance.name : 'Select an instance'}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
          <Command>
            <CommandInput placeholder="Search instances..." />
            <CommandList>
              <CommandEmpty>No instance found.</CommandEmpty>
              <CommandGroup>
                {instances.map((instance) => (
                  <CommandItem
                    key={instance.instanceId}
                    value={instance.name}
                    onSelect={() => {
                      setSelectedInstanceId(instance.instanceId);
                      setInstancePickerOpen(false);
                    }}
                    className="cursor-pointer"
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        selectedInstanceId === instance.instanceId ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    {instance.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  };

  const handleSyncCompany = (company: Company) => syncCompanyAction(company, isDemo, apiPost);

  const handleCompanyIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (validateCompanyId(value)) {
      setFormData({ ...formData, companyId: value });
    }
  };

  const handleCancelBankDetails = () => {
    if (isNewCompanyFlow) {
      setIsNewCompanyFlow(false);
      setFormData(INITIAL_FORM_DATA);
      toast.info(editingPendingCompanyId ? 'Company setup cancelled' : 'Company creation cancelled');
    }
    setIsManageBankDetailsDialogOpen(false);
    setSelectedCompany(null);
    setSelectedProduct('');
    setSelectedInstanceId('');
    setSubLedgers([]);
    setSelectedSubLedger('');
    setEditingPendingCompanyId(null);
    setBankDetails([
      { id: '1', currency: '', sortCode: '', accountNumber: '', accountName: '', ibanNumber: '', swiftBicCode: '' },
    ]);
    setIntegrationBankRowErrors({});
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = formatCompanyDateTime;

  // Defence-in-depth guard: show empty state if non-admin slips past the router guard.
  if (!isAdminLoading && !isAdmin) {
    return <AdminOnlyEmptyState />;
  }

  return (
    <div className="flex-1 space-y-6 p-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold tracking-tight">Company Settings</h1>
          <p className="text-muted-foreground mt-2">Manage your companies, integrations, and organisational settings</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => {
              setIsAddMode(true);
              setIsManageIntegrationsDialogOpen(true);
            }}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Company
          </Button>
          <HelpSupportButton screenId="settings-company" />
        </div>
      </div>

      {/* Statistics Cards — hidden in onboarding since the list starts empty */}
      {!isOnboarding && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card className="transition-shadow hover:shadow-md">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Companies</CardTitle>
              <Building2 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{summary?.totalCompanies ?? '—'}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Integrations</CardTitle>
              <Plug className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{summary?.activeIntegrations ?? '—'}</div>
            </CardContent>
          </Card>

          <Card
            className="cursor-pointer transition-shadow hover:shadow-md"
            onClick={() => navigate('/settings/users')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Users</CardTitle>
              <UsersIcon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{summary?.activeUsers ?? '—'}</div>
            </CardContent>
          </Card>

          <Card
            className="cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/settings/templates')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Document Templates</CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{summary?.documentTemplates ?? '—'}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search and Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Companies</CardTitle>
          <CardDescription>Manage your company profiles and settings</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search companies..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={(value: StatusFilter) => setStatusFilter(value)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="pending_setup">Pending Setup</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sortOption} onValueChange={(value: SortOption) => setSortOption(value)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name-asc">Name A-Z</SelectItem>
                <SelectItem value="name-desc">Name Z-A</SelectItem>
                <SelectItem value="newest">Newest</SelectItem>
                <SelectItem value="oldest">Oldest</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Companies Table */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company ID</TableHead>
                  <TableHead>Company Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Currency</TableHead>
                  <TableHead>Integration</TableHead>
                  <TableHead>Linked Database</TableHead>
                  <TableHead>Linked Sub Ledger</TableHead>
                  <TableHead>Users</TableHead>
                  <TableHead>Created Date</TableHead>
                  <TableHead>Last Sync</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isCompaniesLoading ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center py-8">
                      <div className="flex items-center justify-center gap-2">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        <span className="text-muted-foreground">Loading companies...</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : filteredAndSortedCompanies.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center text-muted-foreground py-8">
                      No companies found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAndSortedCompanies.map((company) => (
                    <TableRow
                      key={company.id}
                      className={cn(
                        'cursor-pointer hover:bg-muted/50 transition-colors',
                        company.isSubCompany && 'bg-muted/20 border-l-2 border-l-border/60',
                      )}
                      onClick={() =>
                        company.status === 'pending_setup'
                          ? handleCompletePendingSetup(company)
                          : handleViewCompany(company)
                      }
                    >
                      <TableCell className={cn(company.isSubCompany && 'text-muted-foreground text-sm')}>
                        {company.code || '-'}
                      </TableCell>
                      <TableCell>
                        {company.isSubCompany ? (
                          <div className="flex items-center gap-2 pl-4">
                            <CornerDownRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                            <Avatar className="h-7 w-7">
                              <AvatarImage src={company.logo} />
                              <AvatarFallback className="text-xs">
                                {company.name.substring(0, 2).toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <div className="font-medium text-sm">{company.name}</div>
                              {company.externalCompanyName && (
                                <div className="text-xs text-muted-foreground">{company.externalCompanyName}</div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-3">
                            <Avatar className="h-8 w-8">
                              <AvatarImage src={company.logo} />
                              <AvatarFallback>{company.name.substring(0, 2).toUpperCase()}</AvatarFallback>
                            </Avatar>
                            <div>
                              <div className="font-medium">{company.name}</div>
                              {company.description && (
                                <div className="text-sm text-muted-foreground">{company.description}</div>
                              )}
                            </div>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {company.status === 'active' && <Badge variant="default">Active</Badge>}
                        {company.status === 'pending_setup' && (
                          <Badge className="bg-orange-500/10 text-orange-500 hover:bg-orange-500/20">
                            Pending Setup
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>{company.currencyCode || '-'}</TableCell>
                      <TableCell>
                        <IntegrationStatusBadge status={company.integrationStatus} company={company} />
                      </TableCell>
                      <TableCell>{company.linkedSubscriptionName || '-'}</TableCell>
                      <TableCell>{company.linkedSubLedger || '-'}</TableCell>
                      <TableCell>{company.userCount}</TableCell>
                      <TableCell>{formatDate(company.createdAt)}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {company.lastSyncedAt ? formatDateTime(company.lastSyncedAt) : '—'}
                      </TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {company.status === 'pending_setup' ? (
                              <DropdownMenuItem onClick={() => handleCompletePendingSetup(company)}>
                                <Settings className="h-4 w-4 mr-2" />
                                Complete Setup
                              </DropdownMenuItem>
                            ) : (
                              <>
                                <DropdownMenuItem onClick={() => handleViewCompany(company)}>
                                  <Eye className="h-4 w-4 mr-2" />
                                  View
                                </DropdownMenuItem>
                                {canForceSync && company.integrationStatus !== 'none' && (
                                  <DropdownMenuItem onClick={() => handleSyncCompany(company)}>
                                    <RefreshCw className="h-4 w-4 mr-2" />
                                    Sync
                                  </DropdownMenuItem>
                                )}
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Add Company Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPendingCompanyId ? 'Complete Company Setup' : 'Add New Company'}</DialogTitle>
            <DialogDescription>
              {editingPendingCompanyId
                ? 'Complete the details for this pending company. Saving will activate it.'
                : 'Create a new company profile. You can set up integrations after creation.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="companyId">
                Company ID <span className="text-destructive">*</span>
              </Label>
              <Input
                id="companyId"
                placeholder="Enter company ID (max 7 alphanumeric characters)"
                value={formData.companyId}
                onChange={handleCompanyIdChange}
                maxLength={7}
              />
              <p className="text-xs text-muted-foreground">Alphanumeric characters only, maximum 7 characters</p>
            </div>
            <div className="grid grid-cols-[2fr_1fr] gap-6">
              <div className="space-y-2">
                <Label htmlFor="name">
                  Company Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="name"
                  placeholder="Enter company name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="currency">Currency</Label>
                <Input
                  id="currency"
                  placeholder="Will be imported from integration"
                  value={formData.currencyCode || ''}
                  disabled
                  className="bg-muted cursor-not-allowed"
                />
                <p className="text-xs text-muted-foreground">Imported when integration is enabled</p>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                placeholder="Enter company address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="vatRegistrationNo">VAT Registration No.</Label>
                <Input
                  id="vatRegistrationNo"
                  placeholder="Enter VAT registration number"
                  value={formData.vatRegistrationNo}
                  onChange={(e) => {
                    setFormData({ ...formData, vatRegistrationNo: e.target.value });
                    setCreateValidationWarnings((prev) =>
                      prev.vatRegistrationNo ? { ...prev, vatRegistrationNo: null } : prev,
                    );
                  }}
                  onBlur={(e) => handleCreateFieldBlur('vatRegistrationNo', e.target.value)}
                />
                {createValidationWarnings.vatRegistrationNo && (
                  <p className="text-sm text-amber-600 dark:text-amber-500">
                    {createValidationWarnings.vatRegistrationNo}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="companyRegistrationNo">Company Registration No.</Label>
                <Input
                  id="companyRegistrationNo"
                  placeholder="Enter company registration number"
                  value={formData.companyRegistrationNo}
                  onChange={(e) => {
                    setFormData({ ...formData, companyRegistrationNo: e.target.value });
                    setCreateValidationWarnings((prev) =>
                      prev.companyRegistrationNo ? { ...prev, companyRegistrationNo: null } : prev,
                    );
                  }}
                  onBlur={(e) => handleCreateFieldBlur('companyRegistrationNo', e.target.value)}
                />
                {createValidationWarnings.companyRegistrationNo && (
                  <p className="text-sm text-amber-600 dark:text-amber-500">
                    {createValidationWarnings.companyRegistrationNo}
                  </p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  placeholder="Enter phone number"
                  value={formData.phone}
                  onChange={(e) => {
                    setFormData({ ...formData, phone: e.target.value });
                    setCreateValidationErrors((prev) => (prev.phone ? { ...prev, phone: null } : prev));
                  }}
                  onBlur={(e) => handleCreateFieldBlur('phone', e.target.value)}
                  aria-invalid={createValidationErrors.phone ? true : undefined}
                />
                {createValidationErrors.phone && (
                  <p className="text-sm text-destructive">{createValidationErrors.phone}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter email address"
                  value={formData.email}
                  onChange={(e) => {
                    setFormData({ ...formData, email: e.target.value });
                    setCreateValidationErrors((prev) => (prev.email ? { ...prev, email: null } : prev));
                  }}
                  onBlur={(e) => handleCreateFieldBlur('email', e.target.value)}
                  aria-invalid={createValidationErrors.email ? true : undefined}
                />
                {createValidationErrors.email && (
                  <p className="text-sm text-destructive">{createValidationErrors.email}</p>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="logo">Company Logo</Label>
              {!logoFile ? (
                <div
                  onDragEnter={handleLogoDragEnter}
                  onDragOver={handleLogoDragOver}
                  onDragLeave={handleLogoDragLeave}
                  onDrop={handleLogoDrop}
                  className={cn(
                    'border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer',
                    isDraggingLogo
                      ? 'border-primary bg-primary/5'
                      : 'border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/50',
                  )}
                  onClick={() => logoInputRef.current?.click()}
                >
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/gif,image/svg+xml"
                    onChange={handleLogoInputChange}
                    className="hidden"
                  />
                  <Upload className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
                  <p className="text-sm font-medium mb-1">
                    {isDraggingLogo ? 'Drop logo here' : 'Drag and drop logo here'}
                  </p>
                  <p className="text-xs text-muted-foreground mb-2">or click to browse your computer</p>
                  <p className="text-xs text-muted-foreground">Supported formats: PNG, JPG, GIF, SVG (Max 5MB)</p>
                </div>
              ) : (
                <div className="border rounded-lg p-4 bg-muted/50">
                  <div className="flex items-center gap-4">
                    <div className="relative w-20 h-20 rounded-lg overflow-hidden bg-background border">
                      {logoPreview ? (
                        <img src={logoPreview} alt="Logo preview" className="w-full h-full object-contain" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ImageIcon className="h-8 w-8 text-muted-foreground" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{logoFile.name}</p>
                      <p className="text-sm text-muted-foreground">{(logoFile.size / 1024).toFixed(2)} KB</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={removeLogoFile}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsAddDialogOpen(false);
                setIsAddMode(false);
                setEditingPendingCompanyId(null);
                setIsNewCompanyFlow(false);
                setLogoFile(null);
                setLogoPreview('');
                setFormData(INITIAL_FORM_DATA);
                setSelectedProduct('');
                setSelectedInstanceId('');
                setSelectedSubLedger('');
                setGeneratePerSubLedger(false);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (editingPendingCompanyId) {
                  // Completing a pending_setup company — preserve existing integration data
                  const existingCompany = companies.find((c) => c.id === editingPendingCompanyId);
                  const pendingCompany: Company = {
                    ...(existingCompany ?? {
                      integrationStatus: 'none',
                      userCount: 0,
                      createdAt: new Date().toISOString(),
                    }),
                    id: editingPendingCompanyId,
                    name: formData.name,
                    status: 'pending_setup',
                    logo: logoPreview || existingCompany?.logo || undefined,
                    code: formData.companyId,
                    taxId: formData.taxId,
                  };
                  setSelectedCompany(pendingCompany);
                  setIsAddDialogOpen(false);
                  setIsManageBankDetailsDialogOpen(true);
                } else if (isAddMode) {
                  // In add mode, create pending company and move to Bank Details dialog
                  const pendingCompany: Company = {
                    id: '',
                    name: formData.name,
                    status: formData.status,
                    logo: logoPreview || undefined,
                    description: formData.description || undefined,
                    integrationStatus: 'none',
                    userCount: 0,
                    createdAt: new Date().toISOString(),
                    code: formData.companyId,
                    currencyCode: formData.currencyCode,
                    taxId: formData.taxId,
                  };
                  setSelectedCompany(pendingCompany);
                  setIsNewCompanyFlow(true);
                  setIsAddDialogOpen(false);
                  setIsManageBankDetailsDialogOpen(true);
                } else {
                  // In edit mode (existing flow), save the company
                  handleAddCompany();
                }
              }}
              disabled={
                !formData.companyId ||
                !formData.name ||
                !!createValidationErrors.email ||
                !!createValidationErrors.phone
              }
            >
              Next
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Company Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Company</DialogTitle>
            <DialogDescription>Update company information and settings.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Company Name *</Label>
              <Input
                id="edit-name"
                placeholder="Enter company name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                placeholder="Enter company description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-logo">Logo URL</Label>
              <Input
                id="edit-logo"
                placeholder="Enter logo URL (optional)"
                value={formData.logo}
                onChange={(e) => setFormData({ ...formData, logo: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditCompany} disabled={!formData.name}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Integrations Dialog */}
      <Dialog open={isManageIntegrationsDialogOpen} onOpenChange={setIsManageIntegrationsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manage Integrations</DialogTitle>
            <DialogDescription>
              {isAddMode
                ? 'Select a product and instance to link to your new company.'
                : `Link ${selectedCompany?.name} to an instance.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Only show the product picker when more than one product is available
                (or while loading). When only one product is available it is
                auto-selected above and we skip straight to the Instance picker. */}
            {(productAvailability.Financials === 'loading' ||
              productAvailability.DimensionsvNext === 'loading' ||
              (productAvailability.Financials === 'available' &&
                productAvailability.DimensionsvNext === 'available')) && (
              <div className={cn('space-y-2', isLoadingSubLedgers && 'pointer-events-none opacity-50')}>
                <Label>Product</Label>
                <ProductRadioCards
                  availability={productAvailability}
                  selected={selectedProduct}
                  onSelect={setSelectedProduct}
                />
              </div>
            )}
            {/* When only one product is available show a compact read-only badge */}
            {productAvailability.Financials !== 'loading' &&
              productAvailability.DimensionsvNext !== 'loading' &&
              (productAvailability.Financials === 'available') !==
                (productAvailability.DimensionsvNext === 'available') && (
                <div className="flex items-center gap-2">
                  <Label className="text-muted-foreground text-xs">Product</Label>
                  <Badge variant="secondary" className="text-xs">
                    {selectedProduct === 'Financials' ? 'Access Financials' : 'Access Dimensions'}
                  </Badge>
                </div>
              )}
            {selectedProduct && (
              <div className="space-y-2">
                <Label htmlFor="instance">Instance</Label>
                {renderInstancePicker()}
              </div>
            )}
            {selectedInstanceId && isLoadingSubLedgers && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Loading sub ledgers...</span>
              </div>
            )}
            {selectedInstanceId && !isLoadingSubLedgers && subLedgers.length > 0 && (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="generatePerSubLedger"
                  checked={generatePerSubLedger}
                  onCheckedChange={(checked) => {
                    setGeneratePerSubLedger(checked as boolean);
                    if (!checked) {
                      setSelectedSubLedger('');
                    }
                  }}
                />
                <div className="flex items-center gap-1.5">
                  <Label
                    htmlFor="generatePerSubLedger"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    Generate Companies per Sub Ledger
                  </Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help shrink-0" />
                      </TooltipTrigger>
                      <TooltipContent side="right" className="max-w-64">
                        <p>
                          If a sub ledger represents a legal entity, it is recommended to generate a separate company
                          per sub ledger.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
            )}
            {selectedInstanceId && !isLoadingSubLedgers && generatePerSubLedger && (
              <div className="space-y-2">
                <Label htmlFor="subLedger">Sub Ledger</Label>
                {subLedgersError ? (
                  <div className="text-sm text-destructive">{subLedgersError}</div>
                ) : (
                  <Popover open={subLedgerPickerOpen} onOpenChange={setSubLedgerPickerOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        aria-expanded={subLedgerPickerOpen}
                        aria-haspopup="listbox"
                        className="w-full justify-between h-9 font-normal"
                      >
                        <span className={cn('truncate', !selectedSubLedger && 'text-muted-foreground')}>
                          {selectedSubLedger
                            ? `${selectedSubLedger} - ${subLedgers.find((x) => x.code === selectedSubLedger)?.name || selectedSubLedger}`
                            : 'Select a sub ledger'}
                        </span>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Search sub ledgers..." />
                        <CommandList>
                          <CommandEmpty>No sub ledger found.</CommandEmpty>
                          <CommandGroup>
                            <CommandItem
                              value="— None —"
                              onSelect={() => {
                                setSelectedSubLedger('');
                                setSubLedgerPickerOpen(false);
                              }}
                              className="cursor-pointer"
                            >
                              <Check className={cn('mr-2 h-4 w-4', !selectedSubLedger ? 'opacity-100' : 'opacity-0')} />
                              — None —
                            </CommandItem>
                            {subLedgers.map((subLedger) => {
                              const isAlreadyUsed = alreadyUsedSubLedgerCodes.has(subLedger.code);
                              return (
                                <CommandItem
                                  key={subLedger.code}
                                  value={`${subLedger.code} ${subLedger.name}`}
                                  onSelect={() => {
                                    if (!isAlreadyUsed) {
                                      setSelectedSubLedger(subLedger.code);
                                      setSubLedgerPickerOpen(false);
                                    }
                                  }}
                                  disabled={isAlreadyUsed}
                                  className="cursor-pointer"
                                >
                                  <Check
                                    className={cn(
                                      'mr-2 h-4 w-4',
                                      selectedSubLedger === subLedger.code ? 'opacity-100' : 'opacity-0',
                                    )}
                                  />
                                  {subLedger.code} - {subLedger.name}
                                  {isAlreadyUsed ? ' (already set up)' : ''}
                                </CommandItem>
                              );
                            })}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
            )}
          </div>
          <DialogFooter className="flex justify-between sm:justify-between">
            <div />
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleCancelIntegration}>
                Cancel
              </Button>
              <Button
                onClick={handleNextToIntegration}
                disabled={isLoadingInstances || !selectedInstanceId || isLoadingSubLedgers || !!subLedgersError}
              >
                Next
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Bank Details Dialog */}
      <Dialog open={isManageBankDetailsDialogOpen} onOpenChange={setIsManageBankDetailsDialogOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Manage Bank Details</DialogTitle>
            <DialogDescription>
              {`Add bank account details for ${isAddMode && !editingPendingCompanyId ? formData.name : selectedCompany?.name}.`}
            </DialogDescription>
          </DialogHeader>
          <div className={cn('py-4', isSavingIntegration && 'pointer-events-none opacity-50')}>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[120px]">Currency</TableHead>
                    <TableHead className="w-[120px]">Sort Code</TableHead>
                    <TableHead className="w-[150px]">Account Number</TableHead>
                    <TableHead className="w-[180px]">Account Name</TableHead>
                    <TableHead className="w-[180px]">IBAN Number</TableHead>
                    <TableHead className="w-[150px]">SWIFT/BIC Code</TableHead>
                    <TableHead className="w-[80px] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bankDetails.map((detail) => {
                    const rowErrors = integrationBankRowErrors[detail.id];
                    const currencyError = rowErrors?.currency;
                    const sortCodeError = rowErrors?.sortCode;
                    const accountNumberError = rowErrors?.accountNumber;
                    const accountNameError = rowErrors?.accountName;
                    const ibanError = rowErrors?.ibanNumber;
                    const swiftError = rowErrors?.swiftBicCode;
                    const currencyPlaceholder = integrationCurrenciesLoading ? 'Loading…' : 'Select currency';
                    return (
                      <TableRow key={detail.id}>
                        <TableCell className="align-top">
                          <div>
                            <CurrencyCombobox
                              currencies={integrationCurrencies}
                              value={detail.currency || undefined}
                              onValueChange={(value) => {
                                handleBankDetailChange(detail.id, 'currency', value);
                                handleIntegrationBankRowBlur(detail.id);
                              }}
                              disabled={integrationCurrenciesLoading}
                              placeholder={currencyPlaceholder}
                              invalid={!!currencyError}
                            />
                            {currencyError ? <p className="mt-1 text-xs text-destructive">{currencyError}</p> : null}
                          </div>
                        </TableCell>
                        <TableCell className="align-top">
                          <div>
                            <Input
                              placeholder="12-34-56"
                              value={detail.sortCode}
                              onChange={(e) => handleBankDetailChange(detail.id, 'sortCode', e.target.value)}
                              onBlur={() => handleIntegrationBankRowBlur(detail.id)}
                              maxLength={BANK_DETAIL_MAX_LENGTHS.sortCode}
                              aria-invalid={sortCodeError ? true : undefined}
                              className="h-9"
                            />
                            {sortCodeError ? <p className="mt-1 text-xs text-destructive">{sortCodeError}</p> : null}
                          </div>
                        </TableCell>
                        <TableCell className="align-top">
                          <div>
                            <Input
                              placeholder="12345678"
                              value={detail.accountNumber}
                              onChange={(e) => handleBankDetailChange(detail.id, 'accountNumber', e.target.value)}
                              onBlur={() => handleIntegrationBankRowBlur(detail.id)}
                              maxLength={BANK_DETAIL_MAX_LENGTHS.accountNumber}
                              aria-invalid={accountNumberError ? true : undefined}
                              className="h-9"
                            />
                            {accountNumberError ? (
                              <p className="mt-1 text-xs text-destructive">{accountNumberError}</p>
                            ) : null}
                          </div>
                        </TableCell>
                        <TableCell className="align-top">
                          <div>
                            <Input
                              placeholder="Business Account"
                              value={detail.accountName}
                              onChange={(e) => handleBankDetailChange(detail.id, 'accountName', e.target.value)}
                              onBlur={() => handleIntegrationBankRowBlur(detail.id)}
                              maxLength={BANK_DETAIL_MAX_LENGTHS.accountName}
                              aria-invalid={accountNameError ? true : undefined}
                              className="h-9"
                            />
                            {accountNameError ? (
                              <p className="mt-1 text-xs text-destructive">{accountNameError}</p>
                            ) : null}
                          </div>
                        </TableCell>
                        <TableCell className="align-top">
                          <div>
                            <Input
                              placeholder="GB29 NWBK 6016 1331 9268 19"
                              value={formatIbanDisplay(detail.ibanNumber ?? '')}
                              onChange={(e) => handleBankDetailChange(detail.id, 'ibanNumber', e.target.value)}
                              onBlur={() => handleIntegrationBankRowBlur(detail.id)}
                              // Account for spaces inserted by formatIbanDisplay (cap + spaces between 4-char groups)
                              maxLength={
                                BANK_DETAIL_MAX_LENGTHS.ibanNumber + Math.ceil(BANK_DETAIL_MAX_LENGTHS.ibanNumber / 4)
                              }
                              aria-invalid={ibanError ? true : undefined}
                              className="h-9"
                            />
                            {ibanError ? <p className="mt-1 text-xs text-destructive">{ibanError}</p> : null}
                          </div>
                        </TableCell>
                        <TableCell className="align-top">
                          <div>
                            <Input
                              placeholder="NWBKGB2L"
                              value={detail.swiftBicCode}
                              onChange={(e) => handleBankDetailChange(detail.id, 'swiftBicCode', e.target.value)}
                              onBlur={() => handleIntegrationBankRowBlur(detail.id)}
                              maxLength={BANK_DETAIL_MAX_LENGTHS.swiftBicCode}
                              aria-invalid={swiftError ? true : undefined}
                              className="h-9"
                            />
                            {swiftError ? <p className="mt-1 text-xs text-destructive">{swiftError}</p> : null}
                          </div>
                        </TableCell>
                        <TableCell className="text-right align-top">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRemoveBankDetailRow(detail.id)}
                            disabled={bankDetails.length === 1}
                            className="h-9 w-9"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            <div className="mt-4">
              <Button variant="outline" onClick={handleAddBankDetailRow} className="w-full">
                <Plus className="h-4 w-4 mr-2" />
                Add Bank Detail Row
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelBankDetails}>
              Cancel
            </Button>
            <Button onClick={handleSaveIntegration} disabled={isSavingIntegration || hasIntegrationBankErrors}>
              {isSavingIntegration && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CompanyViewDialog
        open={isViewDialogOpen}
        onOpenChange={setIsViewDialogOpen}
        company={viewCompanyData}
        isDemo={isDemo}
        apiGet={apiGet}
        refetchCompanies={refetchCompanies}
        onSave={handleViewDialogSave}
      />
    </div>
  );
}
